/**
 * Ideas — shared behaviour for the custom home sections.
 *
 * Four tiny custom elements, no dependencies, no framework:
 *   <ia-carousel>  scroll-snap track with arrows, dots and optional autoplay
 *   <ia-tabs>      tab switcher over server-rendered panels
 *   <ia-drawer>    off-canvas panel built on the native <dialog>
 *   <ia-wishlist>  per-browser saved-items toggle
 *
 * Everything degrades gracefully: with JS disabled the track is still a native
 * horizontally scrollable list and every panel and slide remains reachable.
 */

const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
// Must match the bottom-sheet breakpoint in ia-add-to-cart.liquid.
const SMALL_SCREEN = matchMedia('(max-width: 989px)');

/**
 * Write a price into a .money element so the currency converter app converts
 * it again. The app re-converts on an edited text node (characterData) but
 * skips a text node that replaces the old one, which is what textContent does
 * — so edit the existing node when there is one.
 */
function iaSetMoney(el, text) {
  if (!el) return;
  // The app marks a converted price (data-processed) and keeps the first
  // amount it saw (original_price); clear both so the new amount is converted.
  el.removeAttribute('original_price');
  el.removeAttribute('current_price');
  el.removeAttribute('data-processed');
  const node = el.childNodes.length === 1 && el.firstChild.nodeType === 3 ? el.firstChild : null;
  if (node) {
    if (node.data !== text) node.data = text;
  } else {
    el.textContent = text;
  }
}

class IaCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ia-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-ia-prev]');
    this.nextButton = this.querySelector('[data-ia-next]');
    this.dots = Array.from(this.querySelectorAll('[data-ia-dot]'));
    this.perPage = this.hasAttribute('data-page-scroll');

    this.onScroll = this.throttle(() => this.sync());
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.prevButton?.addEventListener('click', () => this.step(-1));
    this.nextButton?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

    this.resizeObserver = new ResizeObserver(() => this.sync());
    this.resizeObserver.observe(this.track);

    this.setupAutoplay();
    this.sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    this.stop();
  }

  get slides() {
    return Array.from(this.track.children);
  }

  /** Distance one arrow press should travel. */
  get stepSize() {
    if (this.perPage) return this.track.clientWidth;
    const [first, second] = this.slides;
    if (!first) return this.track.clientWidth;
    if (second) return Math.round(second.offsetLeft - first.offsetLeft);
    return first.offsetWidth;
  }

  get maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  step(direction) {
    const delta = this.stepSize * direction * (this.isRtl ? -1 : 1);
    const position = this.track.scrollLeft + delta;
    const limit = this.isRtl ? 0 : this.maxScroll;
    const start = this.isRtl ? -this.maxScroll : 0;

    // Wrap around when looping, otherwise clamp at the ends.
    if (this.hasAttribute('data-loop')) {
      if (direction > 0 && Math.abs(this.track.scrollLeft) >= this.maxScroll - 2) {
        this.scrollTo(start);
        return;
      }
      if (direction < 0 && Math.abs(this.track.scrollLeft) <= 2) {
        this.scrollTo(limit);
        return;
      }
    }

    this.scrollTo(position);
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.scrollTo(slide.offsetLeft - this.track.offsetLeft);
  }

  scrollTo(left) {
    this.track.scrollTo({ left, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  sync() {
    const position = Math.abs(this.track.scrollLeft);
    const max = this.maxScroll;
    const atStart = position <= 2;
    const atEnd = position >= max - 2;
    const loops = this.hasAttribute('data-loop');
    const inert = max <= 2;

    const disablePrev = inert || (!loops && atStart);
    const disableNext = inert || (!loops && atEnd);

    if (this.prevButton) this.prevButton.disabled = disablePrev;
    if (this.nextButton) this.nextButton.disabled = disableNext;
    this.toggleAttribute('data-inert', inert);

    // Lets an ancestor (a tab header, say) drive its own shared arrow pair.
    this.dispatchEvent(
      new CustomEvent('ia:carousel:sync', {
        bubbles: true,
        detail: { atStart: disablePrev, atEnd: disableNext, inert },
      })
    );

    if (this.dots.length) {
      const size = this.stepSize || 1;
      const active = Math.min(this.dots.length - 1, Math.round(position / size));
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', String(index === active));
      });
      this.activeIndex = active;
    }
  }

  setupAutoplay() {
    this.interval = Number(this.getAttribute('data-autoplay')) || 0;
    if (!this.interval || REDUCED_MOTION.matches) return;

    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.play());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.play());
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.play()));

    // Only spend cycles while the carousel is actually on screen.
    this.visibilityObserver = new IntersectionObserver((entries) => {
      entries[0].isIntersecting ? this.play() : this.stop();
    });
    this.visibilityObserver.observe(this);
  }

  play() {
    if (!this.interval || this.timer || document.hidden) return;
    this.timer = setInterval(() => this.step(1), this.interval);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  throttle(callback) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        callback();
      });
    };
  }
}

/**
 * Tab switcher.
 *
 * Panels are rendered server-side and toggled with the `hidden` attribute, so
 * every tab's products are in the markup for crawlers and work without JS.
 * Images in hidden panels stay lazy, so switching costs a paint, not a refetch.
 *
 * When a shared arrow pair sits in the tab header (outside the panels), the
 * clicks are forwarded to whichever carousel is currently visible.
 */
class IaTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-ia-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-ia-panel]'));
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab.dataset.iaTab));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });

    // Arrows that live in the header rather than inside a single carousel.
    this.prevButton = this.querySelector('[data-ia-shared-prev]');
    this.nextButton = this.querySelector('[data-ia-shared-next]');
    this.prevButton?.addEventListener('click', () => this.activeCarousel?.step(-1));
    this.nextButton?.addEventListener('click', () => this.activeCarousel?.step(1));
    this.addEventListener('ia:carousel:sync', (event) => {
      if (event.target !== this.activeCarousel) return;
      if (this.prevButton) this.prevButton.disabled = event.detail.atStart;
      if (this.nextButton) this.nextButton.disabled = event.detail.atEnd;
    });
  }

  get activeCarousel() {
    return this.panels.find((panel) => !panel.hidden)?.querySelector('ia-carousel') ?? null;
  }

  onKeydown(event) {
    const offset = { ArrowRight: 1, ArrowLeft: -1, Home: -Infinity, End: Infinity }[event.key];
    if (offset === undefined) return;
    event.preventDefault();
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const next = Math.max(0, Math.min(this.tabs.length - 1, current + offset));
    this.tabs[next]?.focus();
    this.select(this.tabs[next]?.dataset.iaTab);
  }

  select(key) {
    if (key == null) return;

    this.tabs.forEach((tab) => {
      const selected = tab.dataset.iaTab === key;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.iaPanel !== key;
    });

    // Per-tab extras that are not panels (the spotlight's View all links).
    for (const extra of this.querySelectorAll('[data-ia-for]')) {
      extra.hidden = extra.dataset.iaFor !== key;
    }

    this.activeCarousel?.sync();
  }
}

/**
 * Minimal, self-contained saved-items toggle. Persists per browser in
 * localStorage and emits `ia:wishlist` so a real wishlist app — if one is ever
 * installed — can pick the same events up without markup changes.
 */
const WISHLIST_KEY = 'ia:wishlist';

class IaWishlist extends HTMLElement {
  static read() {
    try {
      return new Set(JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'));
    } catch {
      return new Set();
    }
  }

  static write(set) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify([...set]));
    } catch {
      /* private mode — the toggle still works for this page view */
    }
  }

  connectedCallback() {
    this.button = this.querySelector('button');
    this.productId = this.dataset.product;
    if (!this.button || !this.productId) return;

    this.button.setAttribute('aria-pressed', String(IaWishlist.read().has(this.productId)));
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      const saved = IaWishlist.read();
      const next = !saved.has(this.productId);
      next ? saved.add(this.productId) : saved.delete(this.productId);
      IaWishlist.write(saved);
      this.button.setAttribute('aria-pressed', String(next));
      this.dispatchEvent(
        new CustomEvent('ia:wishlist', { bubbles: true, detail: { id: this.productId, saved: next } })
      );
    });
  }
}

/**
 * Off-canvas panel built on the native <dialog>, so focus trapping, Escape and
 * inertness come from the platform rather than from us.
 */
class IaDrawer extends HTMLElement {
  connectedCallback() {
    // Explicit hook, not `querySelector('dialog')`: the header also contains the
    // cart drawer's dialog, and it comes first in the DOM.
    this.dialog = this.querySelector('[data-ia-drawer-dialog]') ?? this.querySelector('dialog');
    if (!this.dialog) return;

    this.querySelectorAll('[data-ia-drawer-open]').forEach((button) =>
      button.addEventListener('click', () => this.open())
    );
    this.querySelectorAll('[data-ia-drawer-close]').forEach((button) =>
      button.addEventListener('click', () => this.close())
    );

    // Clicking the backdrop (the dialog element itself, outside its panel)
    // closes. Ambient, because the drawer spends its slide off to the side:
    // until it lands the pointer is over the backdrop wherever it was, the
    // click that opened the drawer included.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close({ ambient: true });
    });

    // Escape fires `cancel` before `close`; intercept it so the drawer still
    // plays its slide-out instead of vanishing.
    this.dialog.addEventListener('cancel', (event) => {
      if (this.closing) return;
      event.preventDefault();
      this.close({ ambient: true });
    });

    this.dialog.addEventListener('close', () => {
      this.closing = false;
      this.opening = false;
      this.dialog.classList.remove('ia-menu--closing', 'ia-menu--docked');
      document.documentElement.classList.remove('ia-dock-menu-open');
      document.documentElement.style.removeProperty('overflow');
    });

    this.setupMenu();
  }

  open() {
    if (this.dialog.open) return;

    // Desktop opens on a pane so the drawer is never half empty; a phone starts
    // on the rail, which is the level it expects to land on. Chosen before the
    // dialog goes up, so every open looks the same to the animation below.
    if (SMALL_SCREEN.matches) this.goTo(null, null);
    else this.goTo(this.roots?.[0]?.dataset.iaRoot ?? null, null);

    // With the mobile bottom bar on screen the drawer opens non-modal: a modal
    // dialog makes everything outside it inert, and the bar has to stay
    // usable over the drawer (switch to Account, close with Menu). The bar
    // takes over what the modal gave for free: the tap outside and Escape.
    const dock = document.querySelector('ia-dock');
    if (dock?.isShown?.()) {
      this.dialog.classList.add('ia-menu--docked');
      document.documentElement.classList.add('ia-dock-menu-open');
      this.dialog.show();
    } else {
      this.dialog.showModal();
    }
    document.documentElement.style.setProperty('overflow', 'hidden');

    // Ambient dismissals are held off until the slide lands: for its duration
    // the panel is still off to the side, so a pointer anywhere over the drawer
    // is really over the backdrop, and the opening click would shut it again.
    this.opening = !REDUCED_MOTION.matches;
    const landed = (event) => {
      if (event.target !== this.dialog) return;
      this.dialog.removeEventListener('animationend', landed);
      this.opening = false;
    };
    this.dialog.addEventListener('animationend', landed);
    // Fallback in case the animation is never delivered.
    setTimeout(() => (this.opening = false), 600);
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.ambient] - Set for a dismissal the reader did not
   *   aim at the drawer — the backdrop or Escape — which is ignored while the
   *   drawer is still sliding in.
   */
  close({ ambient = false } = {}) {
    if (!this.dialog.open || this.closing) return;
    if (ambient && this.opening) return;

    if (REDUCED_MOTION.matches) {
      this.dialog.close();
      return;
    }

    // Let the closing animation finish before the dialog leaves the top layer.
    this.closing = true;
    this.dialog.classList.add('ia-menu--closing');
    // Only the drawer's own slide ends the close: a pane's fade-in bubbles an
    // `animationend` here too, and it would cut the slide short.
    const done = (event) => {
      if (event && event.target !== this.dialog) return;
      this.dialog.removeEventListener('animationend', done);
      this.dialog.close();
    };
    this.dialog.addEventListener('animationend', done);
    // Fallback in case the animation is never delivered.
    setTimeout(() => {
      if (this.closing) done();
    }, 600);
  }

  /** The mega-menu rail. Absent on drawers that are a plain panel. */
  setupMenu() {
    this.roots = Array.from(this.querySelectorAll('[data-ia-root]'));
    this.panes = Array.from(this.querySelectorAll('[data-ia-pane]'));
    if (!this.roots.length) return;

    this.subs = Array.from(this.querySelectorAll('[data-ia-sub]'));
    this.subPanes = Array.from(this.querySelectorAll('[data-ia-sub-pane]'));
    this.masked = new Set();
    this.level = 0;

    // From here a class carries visibility rather than the `hidden` attribute:
    // a class can be transitioned. The attribute ships in the markup so that no
    // pane is ever on screen before this runs.
    for (const pane of [...this.panes, ...this.subPanes]) pane.hidden = false;

    const hoverOpens = this.dialog.hasAttribute('data-hover');

    // Hover intent: sweeping the pointer down the rail to reach a lower item
    // shouldn't flash every pane on the way past.
    const delay = Number(this.dialog.dataset.hoverDelay) || 0;

    for (const root of this.roots) {
      root.addEventListener('click', () => {
        // Phone: the title is a plain link to its collection, the rest of the
        // row (the toggle) opens the level. Opening it as well would flash the
        // pane while the page navigates away.
        if (root.tagName === 'A' && SMALL_SCREEN.matches) return;
        clearTimeout(this.hoverTimer);
        this.goTo(root.dataset.iaRoot, null);
      });

      if (!hoverOpens) continue;

      root.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || SMALL_SCREEN.matches) return;
        clearTimeout(this.hoverTimer);
        this.hoverTimer = setTimeout(() => this.goTo(root.dataset.iaRoot, null), delay);
      });

      root.addEventListener('pointerleave', () => clearTimeout(this.hoverTimer));
    }

    // The third level is a phone-only step: on desktop this button is hidden
    // and its links are already on screen under the column heading.
    for (const sub of this.subs) {
      const key = sub.dataset.iaSub;
      sub.addEventListener('click', () => this.goTo(key.split('-')[0], key));
    }

    this.querySelectorAll('[data-ia-back]').forEach((button) =>
      button.addEventListener('click', () => this.goTo(null, null))
    );

    this.querySelectorAll('[data-ia-sub-back]').forEach((button) =>
      button.addEventListener('click', () => this.goTo(this.rootKey, null))
    );

    // Crossing the breakpoint mid-browse would leave the wrong level showing:
    // desktop has no rail-only state, and a phone has no pane-beside-rail one.
    SMALL_SCREEN.addEventListener('change', (event) => {
      if (!this.dialog.open) return;
      if (event.matches) this.goTo(null, null);
      else this.goTo(this.rootKey ?? this.roots[0]?.dataset.iaRoot ?? null, null);
    });

    this.goTo(null, null);
  }

  /**
   * Move to a level: no root is the rail, a root is its section, a sub key is
   * the sub-section inside it. Every pane stays mounted, so a move is a
   * transition between two layers rather than a re-render.
   */
  goTo(rootKey, subKey) {
    if (!this.roots?.length) return;

    this.rootKey = rootKey ?? null;
    this.subKey = subKey ?? null;

    let pane = null;
    let subPane = null;

    for (const root of this.roots) {
      root.setAttribute('aria-expanded', String(root.dataset.iaRoot === this.rootKey));
    }

    for (const el of this.panes) {
      const on = el.dataset.iaPane === this.rootKey;
      el.classList.toggle('is-active', on);
      if (on) pane = el;
    }

    for (const sub of this.subs) {
      sub.setAttribute('aria-expanded', String(sub.dataset.iaSub === this.subKey));
    }

    for (const el of this.subPanes) {
      const on = el.dataset.iaSubPane === this.subKey;
      el.classList.toggle('is-active', on);
      if (on) subPane = el;
    }

    const level = subPane ? 2 : pane ? 1 : 0;
    const entering = subPane ?? pane;

    // A level opened fresh starts at the top; stepping back to one keeps the
    // place the reader left it at.
    if (entering && (level > this.level || entering !== this.entered)) {
      entering.querySelector(':scope > .ia-menu__pane-scroll')?.scrollTo(0, 0);
    }

    this.dialog.dataset.level = String(level);
    this.level = level;
    this.entered = entering;

    // A covered level is still in the DOM, so on a phone it has to be put out
    // of reach by hand — the level above it only covers it visually.
    this.mask(SMALL_SCREEN.matches ? entering : null);
  }

  /**
   * Make everything outside `keep` inert by walking up from it and inerting the
   * siblings at each step. Called with nothing to release them all again.
   */
  mask(keep) {
    for (const el of this.masked) el.inert = false;
    this.masked.clear();
    if (!keep) return;

    for (let node = keep; node && node !== this.dialog; node = node.parentElement) {
      for (const sibling of node.parentElement?.children ?? []) {
        if (sibling === node) continue;
        sibling.inert = true;
        this.masked.add(sibling);
      }
    }
  }
}

/**
 * Progressive enhancement for the product card's add-to-cart form.
 *
 * Without JS the form posts normally and Shopify redirects to the cart. With
 * JS it posts to the cart AJAX API and announces a `cart:update` event in the
 * shape the theme's own components expect, so the cart bubble and the cart
 * drawer refresh themselves — no bespoke wiring on either side.
 */
class IaAddToCart extends HTMLElement {
  /*
   * Nothing inside the card is cached. Filtering morphs the section in place:
   * this element survives while its form, panel and swatches can be swapped
   * for new nodes, and connectedCallback does not run again. Listeners held on
   * the old nodes went with them, which left every quick-add on a filtered
   * grid dead. So events are delegated from this element and every part is
   * looked up when it is needed.
   */
  get form() {
    return this.querySelector('form');
  }

  get button() {
    return this.querySelector('[data-ia-atc]');
  }

  get label() {
    return this.querySelector('[data-ia-atc-label]');
  }

  get tip() {
    return this.querySelector('[data-ia-atc-tip]');
  }

  get panel() {
    return this.querySelector('[data-ia-atc-panel]');
  }

  get variantInput() {
    return this.querySelector('[data-ia-variant-id]');
  }

  get optionRows() {
    return Array.from(this.querySelectorAll('[data-ia-option]'));
  }

  /** Parsed once per payload node, so a swapped-in product reads its own. */
  get variants() {
    const payload = this.querySelector('[data-ia-variants]');
    if (payload !== this.variantsSource) {
      this.variantsSource = payload;
      try {
        this.variantsCache = JSON.parse(payload?.textContent || '[]');
      } catch {
        this.variantsCache = [];
      }
    }
    return this.variantsCache;
  }

  /** Choices belong to one panel; a new panel starts with none. */
  get selected() {
    const panel = this.panel;
    if (panel !== this.selectedFor) {
      this.selectedFor = panel;
      this.selectedValues = new Array(this.optionRows.length).fill(null);
    }
    return this.selectedValues;
  }

  connectedCallback() {
    // Moving a card (a morph reorders them) reconnects it; bind only once.
    if (this.bound) return;
    this.bound = true;

    this.addEventListener('submit', (event) => this.onSubmit(event));
    this.addEventListener('click', (event) => this.onClick(event));
    // 'close' does not bubble; a capturing listener still hears it.
    this.addEventListener('close', (event) => {
      if (event.target === this.panel) this.onPanelClose();
    }, true);

    // Crossing the breakpoint while open would leave the wrong presentation.
    SMALL_SCREEN.addEventListener('change', () => {
      if (!this.isConnected || !this.panel?.open) return;
      this.closePanel();
      this.openPanel();
    });
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-ia-atc-open]')) return this.openPanel();
    if (target.closest('[data-ia-atc-close]')) return this.closePanel();

    // Tapping the dimmed area behind the sheet closes it.
    if (target === this.panel) {
      if (this.isSheet) this.closePanel();
      return;
    }

    const step = target.closest('[data-ia-qty]');
    if (step) return this.stepQuantity(Number(step.dataset.iaQty));

    const swatch = target.closest('[data-ia-value]');
    const row = swatch?.closest('[data-ia-option]');
    if (swatch && row) this.choose(Number(row.dataset.iaOption), swatch.dataset.iaValue, row);
  }

  get isSheet() {
    return this.hasAttribute('data-sheet') && SMALL_SCREEN.matches;
  }

  openPanel() {
    const panel = this.panel;
    if (!panel) return;
    this.preselect();
    // Dims unbuyable values for whatever product the panel now holds.
    this.markAvailability();

    // Modal on mobile so the sheet reaches the top layer and gets a backdrop;
    // non-modal on desktop so it stays inline in the card.
    if (this.isSheet) {
      if (!panel.open) panel.showModal();
      document.documentElement.style.setProperty('overflow', 'hidden');
    } else {
      if (!panel.open) panel.show();
      const opener = this.querySelector('[data-ia-atc-open]');
      if (opener) opener.hidden = true;
    }
    (panel.querySelector('[data-ia-value][aria-pressed="true"]') || panel.querySelector('[data-ia-value]'))?.focus();
  }

  /**
   * Collection cards (data-preselect) open with the first in-stock variant
   * already chosen, so one tap on Add is enough. Skipped once the shopper has
   * picked anything, and for a variant whose value is not among the swatches
   * shown (the swatch cap can leave it out) — the next in-stock one is used.
   */
  preselect() {
    if (!this.hasAttribute('data-preselect')) return;
    const selected = this.selected;
    if (selected.some((value) => value !== null)) return;

    const rows = this.optionRows;
    const shown = (row, value) => [...row.querySelectorAll('[data-ia-value]')].some((s) => s.dataset.iaValue === value);
    const first = this.variants.find(
      (variant) => variant.a && rows.every((row) => shown(row, variant.o[Number(row.dataset.iaOption)]))
    );
    if (!first) return;

    for (const row of rows) {
      const index = Number(row.dataset.iaOption);
      selected[index] = first.o[index];
      this.paintRow(index, row);
    }
    this.resolveVariant();
  }

  closePanel() {
    if (this.panel?.open) this.panel.close();
  }

  onPanelClose() {
    document.documentElement.style.removeProperty('overflow');
    const opener = this.querySelector('[data-ia-atc-open]');
    if (opener) opener.hidden = false;
  }

  choose(index, value, row) {
    const selected = this.selected;
    selected[index] = selected[index] === value ? null : value;
    this.paintRow(index, row);

    this.markAvailability();
    this.resolveVariant();
  }

  /**
   * Puts the chosen variant's price, compare price and discount on the card,
   * or the product's own (from data attributes set by Liquid) when nothing is
   * resolved. Writes go through iaSetMoney so the currency app reconverts.
   */
  paintPrice(variant) {
    const card = this.closest('.ia-card');
    const now = card?.querySelector('[data-ia-card-price]');
    if (!now) return;

    const price = variant?.p ?? now.dataset.price;
    const compare = (variant ? variant.c : now.dataset.compare) || '';
    const discount = Number(variant ? variant.d : now.dataset.discount) || 0;
    if (!price) return;

    iaSetMoney(now.querySelector('.money') || now, price);
    now.classList.toggle('ia-card__price-now--sale', Boolean(compare));

    const was = card.querySelector('[data-ia-card-compare]');
    if (was) {
      was.hidden = !compare;
      if (compare) iaSetMoney(was.querySelector('.money') || was, compare);
    }

    const badge = card.querySelector('[data-ia-card-discount]');
    if (badge) {
      badge.hidden = discount <= 0;
      if (discount > 0) badge.textContent = `-${discount}%`;
    }
  }

  /**
   * Shows the variant's own picture on the card (`i`/`s` in the payload exist
   * only when it differs), or the product's again when there is none. A gallery
   * card scrolls to that picture's slide instead.
   */
  paintImage(variant) {
    const card = this.closest('.ia-card');
    if (!card) return;

    const gallery = card.querySelector('ia-carousel.ia-card__gallery');
    if (gallery) {
      if (!variant?.m) return;
      const slides = [...gallery.querySelectorAll('[data-ia-media-src]')];
      const index = slides.findIndex((slide) => slide.dataset.iaMediaSrc === variant.m);
      if (index >= 0) gallery.goTo?.(index);
      return;
    }

    const hover = card.querySelector('.ia-card__link img.ia-card__img--hover');
    if (hover) this.swapPicture(hover, variant?.i ? variant.hi : null, variant?.i ? variant.hs : null);

    const img = card.querySelector('.ia-card__link img.ia-card__img:not(.ia-card__img--hover)');
    if (!img) return;
    // The product's own picture, kept on first swap. A morph hands over a fresh
    // node (or rewrites the attributes), which clears these with it.
    if (!img.hasAttribute('data-ia-orig-src')) {
      img.setAttribute('data-ia-orig-src', img.getAttribute('src') || '');
      img.setAttribute('data-ia-orig-srcset', img.getAttribute('srcset') || '');
    }

    const src = variant?.i || img.getAttribute('data-ia-orig-src');
    const srcset = variant?.i ? variant.s : img.getAttribute('data-ia-orig-srcset');
    if (!src || img.getAttribute('src') === src) return;
    // srcset first: with it still pointing at the old picture the browser
    // would pick from that list and ignore the new src.
    if (srcset) img.setAttribute('srcset', srcset);
    else img.removeAttribute('srcset');
    img.setAttribute('src', src);
  }

  /**
   * Points a hover picture at a variant's next photo, or back at the card's own
   * (src null). Until the first hover it is unloaded, its source parked in
   * data-src/data-srcset (see the pointerover loader), so that is what changes
   * then — swapping src early would fetch a picture nobody has asked for.
   */
  swapPicture(img, src, srcset) {
    const parked = img.hasAttribute('data-src') || img.hasAttribute('data-srcset');
    const srcAttr = parked ? 'data-src' : 'src';
    const setAttr = parked ? 'data-srcset' : 'srcset';
    if (!img.hasAttribute('data-ia-orig-hover')) {
      img.setAttribute('data-ia-orig-hover', JSON.stringify([img.getAttribute(srcAttr) || '', img.getAttribute(setAttr) || '']));
    }
    const [origSrc, origSet] = JSON.parse(img.getAttribute('data-ia-orig-hover'));
    const nextSrc = src || origSrc;
    const nextSet = src ? srcset : origSet;
    if (!nextSrc || img.getAttribute(srcAttr) === nextSrc) return;
    if (nextSet) img.setAttribute(setAttr, nextSet);
    else img.removeAttribute(setAttr);
    img.setAttribute(srcAttr, nextSrc);
  }

  /** Reflects one option's choice on its swatches and its "Size: M" readout. */
  paintRow(index, row) {
    const value = this.selected[index];
    for (const swatch of row.querySelectorAll('[data-ia-value]')) {
      swatch.setAttribute('aria-pressed', String(swatch.dataset.iaValue === value));
    }
    const readout = this.querySelector(`[data-ia-selected="${index}"]`);
    if (readout) readout.textContent = value ?? '';
  }

  /** Dims values that cannot be combined with what is already chosen. */
  markAvailability() {
    const selected = this.selected;
    const variants = this.variants;
    for (const row of this.optionRows) {
      const index = Number(row.dataset.iaOption);
      for (const swatch of row.querySelectorAll('[data-ia-value]')) {
        const candidate = selected.map((value, i) => (i === index ? swatch.dataset.iaValue : value));
        const reachable = variants.some(
          (variant) => variant.a && candidate.every((value, i) => value === null || variant.o[i] === value)
        );
        swatch.toggleAttribute('data-unavailable', !reachable);
      }
    }
  }

  resolveVariant() {
    const selected = this.selected;
    const complete = selected.every((value) => value !== null);
    const match = complete
      ? this.variants.find((variant) => selected.every((value, i) => variant.o[i] === value))
      : null;
    const ready = Boolean(match && match.a);

    if (ready && this.variantInput) this.variantInput.value = match.id;
    if (this.button) this.button.disabled = !ready;
    // A sold-out match still shows its own price; no match shows the product's.
    this.paintPrice(match);
    // A complete match decides the picture (its own, or the product's when it
    // shares it); before that, a partial choice can: a color before a size.
    const partial = selected.some((value) => value !== null)
      ? this.variants.find((variant) => variant.i && selected.every((value, i) => value === null || variant.o[i] === value))
      : null;
    this.paintImage(match || partial);

    // The wording lives in the tooltip, so the button can stay icon-only.
    const text = ready ? this.addLabel : match ? this.soldOutLabel : this.selectLabel;
    this.setActionText(text);
  }

  /**
   * Updates the tooltip and the accessible name only. The visible label is left
   * alone so a disabled button never reads "Select a size" on its face.
   */
  setActionText(text) {
    this.tip?.setAttribute('data-tip', text);
    this.button.setAttribute('aria-label', text);
  }

  /** Briefly swaps the visible label, for the post-add confirmation. */
  flashLabel(text, restoreAfter) {
    if (!this.label) return;
    const previous = this.label.textContent;
    this.label.textContent = text;
    setTimeout(() => (this.label.textContent = previous), restoreAfter);
  }

  get addLabel() {
    return this.dataset.addLabel || 'Add to cart';
  }

  get soldOutLabel() {
    return this.dataset.soldOutLabel || 'Sold out';
  }

  get selectLabel() {
    return this.dataset.selectLabel || 'Select an option';
  }

  stepQuantity(delta) {
    const input = this.querySelector('[data-ia-qty-input]');
    if (!input) return;
    input.value = Math.max(1, (Number(input.value) || 1) + delta);
  }

  async onSubmit(event) {
    // Fall back to the native post if the theme globals are unavailable.
    if (typeof Theme === 'undefined' || !Theme.routes?.cart_add_url) return;
    event.preventDefault();
    if (this.busy) return;

    this.setBusy(true);
    const original = this.tip?.getAttribute('data-tip') ?? this.label?.textContent;

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(this.form),
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);

      const cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();

      if (Theme.translations?.added) {
        this.setActionText(Theme.translations.added);
        this.flashLabel(Theme.translations.added, 1800);
        setTimeout(() => this.setActionText(original), 1800);
      }

      if (this.panel?.open) setTimeout(() => this.closePanel(), 600);

      // Icon-only buttons have no label for flashLabel to change, so a
      // successful add showed nothing on the card itself. Swap in a tick.
      this.classList.add('is-added');
      clearTimeout(this.addedTimer);
      this.addedTimer = setTimeout(() => this.classList.remove('is-added'), 1800);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id || 'ia-add-to-cart',
            data: { source: 'ia-add-to-cart', itemCount: cart.item_count, productId: added.product_id },
          },
        })
      );

      this.revealCart();
    } catch (error) {
      console.error('[ia-add-to-cart]', error);
      // Let the shopper complete the action the reliable way.
      this.form.submit();
    } finally {
      this.setBusy(false);
    }
  }

  /**
   * Slide the cart drawer out so the addition is visible.
   *
   * Skipped when the drawer carries `auto-open`: it opens itself off the same
   * cart:update event, and calling open() as well would fight its animation.
   */
  revealCart() {
    if (!this.hasAttribute('data-open-cart')) return;

    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.hasAttribute('auto-open')) return;

    // The panel's close animation is still running; let it land first.
    setTimeout(() => drawer.open?.(), this.panel?.open ? 700 : 0);
  }

  setBusy(state) {
    this.busy = state;
    this.button?.setAttribute('aria-busy', String(state));
  }
}

/**
 * "Show more" pagination for the collection grid.
 *
 * Appends the next page in place through the Section Rendering API. Without JS
 * the button is an ordinary link to page 2, so the whole collection stays
 * reachable and crawlable.
 */
class IaLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-ia-more]');
    this.grid = document.getElementById(this.dataset.grid);
    if (!this.button || !this.grid) return;

    this.count = this.querySelector('[data-ia-count]');
    this.bar = this.querySelector('[data-ia-progress]');
    this.button.addEventListener('click', (event) => this.load(event));
  }

  async load(event) {
    const next = this.dataset.next;
    if (!next || this.busy) return;

    event.preventDefault();
    this.busy = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(next, location.origin);
      url.searchParams.set('section_id', this.dataset.section);

      const response = await fetch(url);
      if (!response.ok) throw new Error(response.statusText);
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');

      const incoming = page.getElementById(this.dataset.grid);
      if (!incoming) throw new Error('grid missing from response');
      this.grid.append(...incoming.children);

      // The fetched page carries its own next link; when it has none we are done.
      const control = page.querySelector(`ia-load-more[data-grid="${this.dataset.grid}"]`);
      const following = control?.dataset.next;
      if (following) {
        this.dataset.next = following;
        this.button.href = following;
      } else {
        this.remove();
        return;
      }

      this.report(control);
    } catch (error) {
      console.error('[ia-load-more]', error);
      // Fall back to a normal navigation rather than stranding the shopper.
      location.assign(next);
    } finally {
      this.busy = false;
      this.button.removeAttribute('aria-busy');
    }
  }

  /** Mirrors the fetched page's counter and progress bar onto this one. */
  report(control) {
    const shown = control?.querySelector('[data-ia-count]')?.textContent;
    if (shown && this.count) this.count.textContent = shown;

    const value = control?.querySelector('[data-ia-progress]')?.style.getPropertyValue('--ia-progress');
    if (value && this.bar) this.bar.style.setProperty('--ia-progress', value);
  }
}

/**
 * Countdown to a deadline. Renders server-side with the correct figures already
 * in place, so there is no flash of zeroes, and ticks from there.
 */
class IaCountdown extends HTMLElement {
  connectedCallback() {
    const until = Date.parse(this.dataset.until);
    if (Number.isNaN(until)) return;

    this.until = until;
    this.parts = {};
    for (const unit of ['days', 'hours', 'minutes', 'seconds']) {
      const node = this.querySelector(`[data-ia-${unit}]`);
      if (node) this.parts[unit] = node;
    }

    this.tick();
    // Only run while on screen — a background tab or a scrolled-past timer
    // has no reason to wake the main thread every second.
    this.observer = new IntersectionObserver((entries) => {
      entries[0].isIntersecting ? this.start() : this.stop();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stop();
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), 1000);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
  }

  tick() {
    const remaining = Math.max(0, this.until - Date.now());
    const seconds = Math.floor(remaining / 1000);
    const pad = (n) => String(n).padStart(2, '0');

    this.set('days', Math.floor(seconds / 86400));
    this.set('hours', pad(Math.floor(seconds / 3600) % 24));
    this.set('minutes', pad(Math.floor(seconds / 60) % 60));
    this.set('seconds', pad(seconds % 60));

    if (remaining === 0) {
      this.stop();
      this.toggleAttribute('data-expired', true);
    }
  }

  set(unit, value) {
    const node = this.parts[unit];
    if (node && node.textContent !== String(value)) node.textContent = value;
  }
}

/**
 * Grid density switcher for the collection page.
 *
 * Writes --ia-cols onto the grid and remembers the choice for the session. The
 * section also applies the stored value inline before paint, so returning to
 * the page doesn't flash the default column count first.
 */
const VIEW_KEY = 'ia:plp-cols';

class IaView extends HTMLElement {
  connectedCallback() {
    this.grid = document.getElementById(this.dataset.target);
    this.buttons = Array.from(this.querySelectorAll('[data-ia-view]'));
    if (!this.grid || !this.buttons.length) return;

    // Phone and desktop each drive their own column variable and remember
    // their own choice, so switching device does not inherit the other's.
    this.variable = this.dataset.var || '--ia-cols';
    this.storageKey = `${VIEW_KEY}:${this.dataset.key || 'cols'}`;

    let stored = null;
    try {
      stored = sessionStorage.getItem(this.storageKey);
    } catch {
      /* private mode — the switcher still works for this page view */
    }
    this.choice = stored && this.buttons.some((b) => b.dataset.iaView === stored) ? stored : null;
    if (this.choice) this.apply(this.choice);
    else this.mark(this.grid.style.getPropertyValue(this.variable).trim() || this.dataset.current);

    // Filtering morphs the section, which drops the inline column count the
    // server never knew about. Put it back rather than snapping to the default.
    this.observer = new MutationObserver(() => {
      if (this.choice && this.grid.style.getPropertyValue(this.variable).trim() !== this.choice) this.apply(this.choice);
    });
    this.observer.observe(this.grid, { attributeFilter: ['style'] });

    for (const button of this.buttons) {
      button.addEventListener('click', () => {
        this.choice = button.dataset.iaView;
        this.apply(this.choice);
        try {
          sessionStorage.setItem(this.storageKey, this.choice);
        } catch {
          /* nothing to persist to; the choice still applies now */
        }
      });
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  apply(columns) {
    this.grid.style.setProperty(this.variable, columns);
    this.mark(columns);
  }

  mark(columns) {
    for (const button of this.buttons) {
      button.setAttribute('aria-pressed', String(button.dataset.iaView === String(columns)));
    }
  }
}

/**
 * Bottom sheet.
 *
 * A native <dialog> so it lands in the top layer and escapes the scroll
 * container the toolbar sits in. Triggers live anywhere on the page and are
 * matched by the sheet's id, because the filter and sort buttons sit in the
 * toolbar while the sheets themselves render at the end of the section.
 */
class IaSheet extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.key = this.dataset.sheet;
    document.addEventListener('click', this.onDocumentClick);

    for (const button of this.querySelectorAll('[data-ia-sheet-close]')) {
      button.addEventListener('click', () => this.close());
    }

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('cancel', (event) => {
      if (this.closing) return;
      event.preventDefault();
      this.close();
    });

    this.dialog.addEventListener('close', () => {
      this.closing = false;
      this.dialog.classList.remove('is-closing');
      document.documentElement.style.removeProperty('overflow');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(`[data-ia-sheet-open="${this.key}"]`)) return;
    this.open();
  };

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  close() {
    if (!this.dialog.open || this.closing) return;

    if (REDUCED_MOTION.matches) {
      this.dialog.close();
      return;
    }

    // Let the slide-out finish before the dialog leaves the top layer.
    this.closing = true;
    this.dialog.classList.add('is-closing');
    const done = (event) => {
      if (event && event.target !== this.dialog) return;
      this.dialog.removeEventListener('animationend', done);
      this.dialog.close();
    };
    this.dialog.addEventListener('animationend', done);
    setTimeout(() => {
      if (this.closing) done();
    }, 500);
  }
}

/**
 * Sizes the account icon to match search and cart.
 *
 * `<shopify-account>` is Shopify's own element: it has a shadow root whose
 * styles target the slotted icon with `!important`, which outranks anything
 * the document can say, and it re-creates the icon after parse so an inline
 * style in the markup does not survive either. Setting the property here,
 * after it renders, is the one thing that sticks.
 */
function sizeAccountIcon() {
  const button = document.querySelector('.ia-header__col--end .account-button');
  if (!button) return;

  const apply = () => {
    const icon = button.querySelector('.account-button__icon');
    if (!icon) return;
    const styles = getComputedStyle(button);
    // --ia-ic is whichever of the mobile and desktop sizes is live.
    const size = styles.getPropertyValue('--ia-ic').trim() || '20px';
    icon.style.setProperty('width', size, 'important');
    icon.style.setProperty('height', size, 'important');
    // The paths paint with currentColor against the faded --color-account-icon
    // this element inherits. Reading the header's own icon color keeps all
    // three the same; reading it off a sibling did not, because those icons
    // fill rather than stroke and the stroke came back 'none'.
    const ink = styles.getPropertyValue('--ia-icon-c').trim() || styles.color;
    if (ink && ink !== 'none') icon.style.setProperty('color', ink, 'important');
  };

  apply();
  new MutationObserver(apply).observe(button, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sizeAccountIcon);
} else {
  sizeAccountIcon();
}

/**
 * Tag chips that filter without leaving the page.
 *
 * The chips mirror values that also exist as checkboxes in the filter rail, so
 * a click ticks the matching checkbox and lets Horizon do the rest — same URL
 * handling, same partial re-render, same everything as using the sidebar. Only
 * this section has to be refreshed by hand, because it sits outside the
 * results section that Horizon re-renders.
 *
 * With no matching checkbox (the filter is not in the rail, or JavaScript is
 * off) the chip stays an ordinary link and navigates.
 */
class IaTagFilter extends HTMLElement {
  connectedCallback() {
    this.section = this.dataset.section;
    this.addEventListener('click', this.onClick);
  }

  onClick = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // The best-selling chip is a sort, not a filter, so it drives the
    // toolbar's sort control. Clicking it again drops back to the default.
    const sorter = target.closest('[data-sort]');
    if (sorter) {
      const select = document.querySelector('select[name="sort_by"]');
      const wanted = sorter.classList.contains('is-on') ? this.defaultSort(select) : sorter.dataset.sort;
      if (!select || !wanted || !select.querySelector(`option[value="${CSS.escape(wanted)}"]`)) return;

      event.preventDefault();
      select.value = wanted;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.refresh();
      return;
    }

    const chip = target.closest('[data-param][data-value]');
    if (!chip) return;

    const input = document.querySelector(
      `input[name="${CSS.escape(chip.dataset.param)}"][value="${CSS.escape(chip.dataset.value)}"]`
    );
    if (!input) return;

    event.preventDefault();
    input.checked = !input.checked;
    // Horizon updates the address bar synchronously off this event, so by the
    // time it returns the URL is already the one this section should re-read.
    input.dispatchEvent(new Event('change', { bubbles: true }));
    this.refresh();
  };

  /** What the collection sorts by with nothing chosen. */
  defaultSort(select) {
    return select?.closest('form')?.dataset.defaultSort ?? select?.options[0]?.value ?? null;
  }

  async refresh() {
    if (!this.section) return;
    try {
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.section);
      const response = await fetch(url);
      if (!response.ok) return;

      const fresh = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('ia-tag-filter');
      if (fresh) this.innerHTML = fresh.innerHTML;
    } catch {
      /* the results still filtered; only the chips are stale */
    }
  }
}

/**
 * Caps infinite scroll.
 *
 * Horizon loads the next page whenever its `viewMoreNext` sentinel scrolls
 * into view, forever. Endless auto-loading buries the footer and makes the
 * page heavier the longer someone browses, so this pauses it after a set
 * number of pages and offers a button instead.
 *
 * Pausing is done by hiding the sentinel rather than unobserving it: a
 * display:none target has no box, so it can never intersect, and showing it
 * again re-arms Horizon's own observer with nothing to re-wire.
 */
class IaInfiniteCap extends HTMLElement {
  /*
   * A filter change does not replace this element: Horizon morphs the section
   * in place, so the element stays connected while its attributes and the grid
   * are swapped underneath it. Everything that can change is therefore read
   * live — caching data-total here is what left "Showing 24 of 1089" on a
   * filter with 511 results.
   */
  static get observedAttributes() {
    return ['data-total'];
  }

  attributeChangedCallback() {
    if (!this.isConnected || !this.sentinel) return;
    // New results: count the pause from where they start, and allow a nudge.
    this.baseline = this.pageCount;
    this.nudgedAt = null;
    this.check();
  }

  get total() {
    return Number(this.dataset.total) || 0;
  }

  get grid() {
    return document.getElementById(this.dataset.grid);
  }

  connectedCallback() {
    this.limit = Number(this.dataset.limit) || 0;
    this.sentinel = this.closest('results-list')?.querySelector('[ref="viewMoreNext"]');
    this.button = this.querySelector('[data-ia-cap-more]');
    this.spinner = this.querySelector('[data-ia-spinner]');
    this.count = this.querySelector('[data-ia-count]');
    this.progress = this.querySelector('[data-ia-progress]');
    if (!this.limit || !this.grid || !this.sentinel) return;

    this.baseline = this.pageCount;

    // Delegated, so a button swapped in by a re-render still works.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-ia-cap-more]')) this.resume();
    });
    // Watch the results, not the grid node: a re-render can swap the grid.
    this.observer = new MutationObserver(() => this.scheduleCheck());
    this.observer.observe(this.closest('results-list') || this.grid.parentElement, { childList: true, subtree: true });
    this.check();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.checkFrame);
    this.checkFrame = null;
  }

  /**
   * At most once a frame. Every check measures the sentinel, and a burst of
   * mutations — an appended page, an app rewriting each price on it — forced
   * a fresh layout of the whole page per mutation. The frame callback still
   * runs before the paint; the skeletons are moved back last straight away.
   */
  scheduleCheck() {
    this.keepGhostsLast();
    if (this.checkFrame) return;
    this.checkFrame = requestAnimationFrame(() => {
      this.checkFrame = null;
      this.check();
    });
  }

  /** Distinct pages currently in the grid. */
  get pageCount() {
    const pages = new Set();
    for (const card of this.grid?.querySelectorAll('[data-page]') ?? []) pages.add(card.dataset.page);
    return pages.size;
  }

  get exhausted() {
    const grid = this.grid;
    if (!grid) return true;
    const last = Number(grid.dataset.lastPage) || 0;
    let highest = 0;
    for (const card of grid.querySelectorAll('[data-page]')) {
      highest = Math.max(highest, Number(card.dataset.page) || 0);
    }
    return last > 0 && highest >= last;
  }

  /** "Showing 36 of 317" and the bar beneath it, as pages arrive. */
  tally() {
    // Not the hidden ones: a merged search's repeats (see paginated-list).
    const shown = this.grid?.querySelectorAll(':scope > [data-page]:not([hidden])').length ?? 0;
    // Refetch the parts too: the morph can hand over fresh nodes.
    this.count = this.querySelector('[data-ia-count]');
    this.progress = this.querySelector('[data-ia-progress]');
    if (this.count) {
      const text = `${this.dataset.showing} ${shown} ${this.dataset.of} ${this.total}`;
      // Only on change: this write is itself a mutation inside the observed
      // results, and an unconditional one would re-trigger check() forever.
      if (this.count.textContent !== text) this.count.textContent = text;
    }
    if (this.progress && this.total > 0) {
      this.progress.style.setProperty('--ia-progress', `${Math.min(100, (shown / this.total) * 100)}%`);
    }
  }

  /**
   * @param {boolean} capped - Auto-loading has hit its limit.
   * @param {boolean} done - There is no next page left.
   */
  show(capped, done) {
    this.sentinel = this.closest('results-list')?.querySelector('[ref="viewMoreNext"]') || this.sentinel;
    this.button = this.querySelector('[data-ia-cap-more]') || this.button;
    this.spinner = this.querySelector('[data-ia-spinner]') || this.spinner;
    if (this.spinner) this.spinner.hidden = capped || done;
    if (this.button) this.button.hidden = !capped || done;
    // The screen-tall loading area (see .ia-plp__more.is-auto) only while
    // pages still arrive on their own; with the button or at the end, the
    // footer is reachable.
    this.classList.toggle('is-auto', !capped && !done);
    // Held hidden while a nudge is in flight (see nudge()).
    if (this.sentinel) this.sentinel.hidden = capped || done || Boolean(this.nudging);
  }

  /** Horizon appends after the last child: put the skeleton cards back at the end. */
  keepGhostsLast() {
    const grid = this.grid;
    const ghosts = grid?.querySelectorAll(':scope > .ia-plp__ghost');
    if (!ghosts?.length || grid.lastElementChild === ghosts[ghosts.length - 1]) return;
    grid.append(...ghosts);
  }

  check() {
    // A mutation callback runs before the next paint, so the new cards are
    // never seen after the skeletons.
    this.keepGhostsLast();
    // Fewer pages than we started from means the results were replaced.
    if (this.pageCount < this.baseline) {
      this.baseline = this.pageCount;
      this.nudgedAt = null;
    }
    this.tally();

    if (this.exhausted) {
      this.settleMergedTotal();
      this.show(false, true);
      return;
    }
    const capped = this.pageCount - this.baseline >= this.limit;
    this.show(capped, false);
    if (!capped) this.nudge();
  }

  /*
   * Horizon only loads the next page when its sentinel ENTERS the viewport.
   * After a filter applied while scrolled down, or a page too short to push
   * it off screen, the sentinel is already in view, no new intersection
   * happens and loading stalls with the spinner showing. Hiding it for a
   * frame and showing it again makes the observer report it afresh. Once per
   * page count, so a sentinel that stays in view cannot loop.
   */
  nudge() {
    const sentinel = this.sentinel;
    if (!sentinel || sentinel.hidden) return;
    const pages = this.pageCount;
    if (this.nudgedAt !== pages) {
      this.nudgedAt = pages;
      this.nudges = 0;
    }
    // A few tries per page: the first can land inside Horizon's filter
    // view-transition, whose observer callback then sees nothing new.
    if (this.nudges >= 3) return;
    const box = sentinel.getBoundingClientRect();
    // The same band Horizon's observer watches (paginated-list rootMargin).
    if (box.top > window.innerHeight + 2500 || box.bottom < -600) return;
    this.nudges += 1;
    clearTimeout(this.nudgeTimer);
    this.nudging = true;
    sentinel.hidden = true;
    // Long enough for the observer to record "not intersecting" first.
    this.nudgeTimer = setTimeout(() => {
      this.nudging = false;
      if (this.pageCount - this.baseline >= this.limit || this.exhausted) return;
      sentinel.hidden = false;
      // Nothing arrived: try again.
      this.nudgeTimer = setTimeout(() => {
        if (this.pageCount === pages) this.nudge();
      }, 1500);
    }, 150);
  }

  resume() {
    this.baseline = this.pageCount;
    this.check();
  }

  /**
   * A merged search (exact and loose matches, data-alt-q) only has an estimate
   * of its total until every page is in; then it is simply what is shown.
   */
  settleMergedTotal() {
    const grid = this.grid;
    if (!grid?.dataset.altQ) return;
    const shown = String(grid.querySelectorAll(':scope > [data-page]:not([hidden])').length);
    // Only on change: setting it re-runs check(), which lands back here.
    if (this.dataset.total === shown) return;
    this.dataset.total = shown;
    const count = this.closest('results-list')?.querySelector('.ia-plp__count strong');
    if (count) count.textContent = shown;
  }
}

/**
 * Filter rail behaviour.
 *
 * Filtering itself stays Horizon's: every input inside is a plain checkbox
 * whose name and value come from Shopify, and `facets-form-component` turns a
 * change into a Section Rendering API call. This element owns only the parts
 * that are presentation — collapsing the rail, remembering which groups were
 * open, searching a long group, revealing a truncated list and the price
 * quick-picks — and re-applies that state after every re-render, because the
 * server has no idea which groups the reader had opened.
 */
const FACETS_KEY = 'ia:plp-facets';

class IaFacets extends HTMLElement {
  connectedCallback() {
    this.state = this.read();

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('change', this.onChange);
    this.addEventListener('toggle', this.onToggle, true);

    this.apply();

    // The section morphs in place on every filter change, so without this the
    // server's `open` attributes would overwrite the reader's choices.
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this, { childList: true, subtree: true, attributeFilter: ['open'] });

    // A rail taller than the window sticks to its bottom edge instead of its
    // top, so scrolling down walks through the whole of it and it pins once
    // its end is reached. Trapping the wheel in an inner scroller is the
    // alternative, and it feels awful next to an endless grid.
    this.measure = () => {
      const tall = this.scrollHeight > window.innerHeight - 80;
      this.classList.toggle('is-tall', tall);
      // Park the rail so its last filter sits just above the fold once the
      // reader has scrolled past it.
      this.style.setProperty('--ia-rail-top', tall ? `${window.innerHeight - this.scrollHeight - 20}px` : '');
    };
    this.sizer = new ResizeObserver(this.measure);
    this.sizer.observe(this);
    window.addEventListener('resize', this.measure);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.sizer?.disconnect();
    window.removeEventListener('resize', this.measure);
    cancelAnimationFrame(this.frame);
  }

  read() {
    try {
      return JSON.parse(sessionStorage.getItem(FACETS_KEY) ?? '{}') ?? {};
    } catch {
      return {};
    }
  }

  save() {
    try {
      sessionStorage.setItem(FACETS_KEY, JSON.stringify(this.state));
    } catch {
      /* private mode — the rail still behaves, it just forgets on reload */
    }
  }

  schedule() {
    if (this.frame || this.applying) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.apply();
    });
  }

  apply() {
    this.applying = true;

    this.collapse(this.state.collapsed === true, false);

    for (const group of this.querySelectorAll('[data-ia-group]')) {
      const stored = this.state[group.dataset.iaGroup];
      // A group holding an active filter stays open whatever was stored.
      if (stored !== undefined && !group.querySelector('input:checked')) group.open = stored;
    }

    this.applying = false;
  }

  collapse(on, persist = true) {
    const rail = this.querySelector('.ia-fx');
    const iconRail = this.querySelector('[data-ia-iconrail]');
    if (!rail || !iconRail) return;

    rail.classList.toggle('ia-fx--collapsed', on);
    iconRail.hidden = !on;
    this.querySelector('[data-ia-rail-toggle]')?.setAttribute('aria-expanded', String(!on));
    this.classList.toggle('is-collapsed', on);

    if (!persist) return;
    this.state.collapsed = on;
    this.save();
  }

  /**
   * Inside the phone sheet, choosing a filter gets the sheet out of the way so
   * the results are visible straight away. The delay lets Horizon read the
   * change off the form before the dialog starts closing.
   */
  onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const sheet = this.closest('ia-sheet');
    if (sheet) setTimeout(() => sheet.close?.(), 160);
  };

  onToggle = (event) => {
    const group = event.target;
    if (!(group instanceof HTMLDetailsElement) || !group.dataset.iaGroup || this.applying) return;
    this.state[group.dataset.iaGroup] = group.open;
    this.save();
  };

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Record the group's next state here, on the click, rather than waiting
    // for `toggle`. The mutation observer runs as a microtask and `toggle` as
    // a task, so leaving it to `toggle` means apply() re-imposes the *old*
    // stored value first and the group snaps straight back shut.
    const summary = target.closest('.ia-fx__sum');
    if (summary) {
      const group = summary.closest('[data-ia-group]');
      if (group) {
        this.state[group.dataset.iaGroup] = !group.open;
        this.save();
      }
      return;
    }

    if (target.closest('[data-ia-rail-toggle]')) {
      this.collapse(!this.querySelector('.ia-fx')?.classList.contains('ia-fx--collapsed'));
      return;
    }

    if (target.closest('[data-ia-rail-open]')) {
      this.collapse(false);
      return;
    }

    const more = target.closest('[data-ia-more]');
    if (more) {
      const body = more.closest('.ia-fx__body');
      const expanded = more.getAttribute('aria-expanded') === 'true';
      for (const row of body?.querySelectorAll('[data-ia-extra]') ?? []) row.hidden = expanded;
      more.setAttribute('aria-expanded', String(!expanded));
      more.textContent = expanded ? more.dataset.moreLabel : more.dataset.lessLabel;
      return;
    }

    const preset = target.closest('[data-ia-price]');
    if (preset) this.setPrice(preset);
  };

  /** Quick-pick price: fill the two inputs, then let the form do the rest. */
  setPrice(button) {
    const body = button.closest('.ia-fx__body');
    const [min, max] = button.dataset.iaPrice.split(',');
    const inputs = body?.querySelectorAll('.ia-fx__pfield input');
    if (!inputs || inputs.length < 2) return;

    const off = button.classList.contains('is-on');
    inputs[0].value = off ? '' : min;
    inputs[1].value = off ? '' : max;
    inputs[1].dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** Type-ahead inside one group. Every value is already in the markup. */
  onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement) || !field.hasAttribute('data-ia-search')) return;

    const body = field.closest('.ia-fx__body');
    const query = field.value.trim().toLowerCase();
    const more = body?.querySelector('[data-ia-more]');
    const showingAll = more?.getAttribute('aria-expanded') === 'true';

    for (const row of body?.querySelectorAll('[data-ia-label]') ?? []) {
      // Searching overrides the truncation: a match below the cut-off would
      // otherwise stay hidden and the group would look empty.
      if (query) row.hidden = !row.dataset.iaLabel.includes(query);
      else row.hidden = row.hasAttribute('data-ia-extra') && !showingAll;
    }

    if (more) more.hidden = query.length > 0;
  };
}

/**
 * One video in the Ideas video section.
 *
 * Nothing is fetched until the frame is close to the viewport: the markup ships
 * `preload="none"`, so a page of these costs a poster image each and no more.
 * Once visible the video plays muted and pauses again the moment it scrolls
 * away, which keeps the decoding to whatever is actually on screen.
 *
 * Two cuts can be supplied, one per breakpoint. Only one of them is ever
 * displayed, and `offsetParent` is what says which — a display:none video is
 * never asked to play, so the cut that does not apply costs nothing at all.
 */
class IaVideo extends HTMLElement {
  connectedCallback() {
    this.videos = [...this.querySelectorAll('video')];

    // The reveal is set up before anything to do with playback, so a frame
    // still waiting on its video — or showing a placeholder in the editor —
    // does not leave its overlay text stuck at zero opacity.
    this.player = new IntersectionObserver(this.onVisible, { threshold: 0.35 });
    this.player.observe(this);

    if (!this.videos.length) return;

    this.soundButton = this.querySelector('[data-ia-sound]');

    // Autoplay is a request, not a promise: a visitor who has asked their
    // system for less motion gets the poster and a play button instead.
    this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.wanted = this.hasAttribute('autoplay') && !this.calm.matches;

    this.soundButton?.addEventListener('click', this.toggleSound);

    // A wide band, so the source has started arriving by the time the frame
    // is actually in view.
    this.loader = new IntersectionObserver(this.onNear, { rootMargin: '600px 0px' });
    this.loader.observe(this);
  }

  disconnectedCallback() {
    this.loader?.disconnect();
    this.player?.disconnect();
  }

  /** The cut this breakpoint shows. display:none leaves offsetParent null. */
  get current() {
    return this.videos.find((video) => video.offsetParent !== null) || null;
  }

  onNear = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.loader.disconnect();
    const video = this.current;
    if (!video) return;
    // Already loading or playing (a top-of-page film the section's inline
    // script started before ia.js ran): load() would throw away what has
    // buffered and restart it — the stutter on the homepage's first film.
    if (video.preload === 'auto' || video.readyState > 0 || !video.paused) return;
    video.preload = 'auto';
    video.load();
  };

  onVisible = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        // The copy rises in once, the first time its film is reached.
        this.classList.add('is-seen');
        if (this.wanted) this.play();
      } else {
        this.pause();
      }
    }
  };

  play = () => {
    const video = this.current;
    if (!video) return;
    if (video.preload !== 'auto') {
      video.preload = 'auto';
      video.load();
    }
    // First start waits for enough buffer to play through (4s at most), so the
    // film does not stop to buffer right after it begins. A top-of-page film
    // the inline script is already starting is left to it.
    if (!this.dataset.iaStarted && video.readyState < 4) {
      if (this.waitingForBuffer) return;
      this.waitingForBuffer = true;
      const go = () => {
        if (!this.waitingForBuffer) return;
        this.waitingForBuffer = false;
        this.dataset.iaStarted = '1';
        this.play();
      };
      video.addEventListener('canplaythrough', go, { once: true });
      setTimeout(go, 4000);
      return;
    }
    this.dataset.iaStarted = '1';
    const started = video.play();
    // A rejected play is ordinary — a phone on low power, a tab in the
    // background — and only means the play button should be offered.
    if (started && typeof started.catch === 'function') {
      started
        .then(() => {
          this.classList.add('is-playing');
        })
        .catch(() => {
          // A refused play is ordinary — a phone on low power, a background
          // tab. The poster simply stays up.
          this.classList.remove('is-playing');
        });
    }
  };

  pause = () => {
    for (const video of this.videos) {
      if (!video.paused) video.pause();
    }
    this.classList.remove('is-playing');
  };

  toggleSound = () => {
    const video = this.current;
    if (!video) return;
    const on = video.muted;
    video.muted = !on;
    if (on) video.volume = 1;
    this.soundButton.setAttribute('aria-pressed', String(on));
    this.soundButton.setAttribute('aria-label', on ? 'Turn the sound off' : 'Turn the sound on');
    // Unmuting is a deliberate act, so make sure something is actually playing.
    if (on) this.play();
  };
}

/**
 * Ideas video section, slideshow mode (sections/ia-video.liquid).
 *
 * One slide at a time in a fixed frame. A video slide moves on when its film
 * ends; an image banner after its own time (data-ms). Nothing moves while the
 * frame is off screen, the tab is hidden, focus is inside it or (opt-in) a
 * pointer rests on it — and nothing moves by itself for a visitor who has
 * asked for less motion. Slides are read live, never cached, so the theme
 * editor can add, remove and reorder blocks underneath it.
 */
class IaFilm extends HTMLElement {
  /** How long before a slide is due its banner starts loading. */
  static WARM_LEAD_MS = 3000;

  connectedCallback() {
    if (!this.bound) {
      this.bound = true;
      this.holds = new Set();
      this.calm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.phone = window.matchMedia('(max-width: 749px)');

      this.addEventListener('click', this.onClick);
      // A swipe can leave a tap behind; it must not follow the banner's link.
      this.addEventListener('click', this.onClickAfterSwipe, true);
      // 'ended' does not bubble; a capturing listener still hears it.
      this.addEventListener('ended', this.onEnded, true);
      // Nor does 'timeupdate': it is how a film says it is about to finish.
      this.addEventListener('timeupdate', this.onTimeUpdate, true);
      // Keyboard focus only: a mouse click on a dot also focuses it, and that
      // must not stop the slideshow for as long as the button keeps focus.
      this.addEventListener('focusin', (event) => {
        if (event.target instanceof Element && event.target.matches(':focus-visible')) this.hold('focus', true);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focus', false);
      });
      if (this.hasAttribute('data-pause-hover')) {
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.hold('hover', true);
        });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse') this.hold('hover', false);
        });
      }
      this.addEventListener('touchstart', this.onSwipeStart, { passive: true });
      // Not passive: a vertical swipe or a wheel turn that steps the slides
      // must keep the page from scrolling as well.
      this.addEventListener('touchmove', this.onSwipeMove, { passive: false });
      this.addEventListener('touchend', this.onSwipeEnd, { passive: true });
      this.addEventListener('wheel', this.onWheel, { passive: false });
    }

    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    document.addEventListener('visibilitychange', this.onPageVisibility);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    // Held until the frame is actually on screen.
    this.holds.add('offscreen');
    this.watcher = new IntersectionObserver(([entry]) => this.hold('offscreen', !entry.isIntersecting), {
      threshold: 0.25,
    });
    this.watcher.observe(this);
    // Only the slide on show is fetched at arrival (its film by ia-video, its
    // picture eagerly from the markup). The next one waits until shortly
    // before it is due (WARM_LEAD_MS), so it never competes with the opening
    // film or banner for the first seconds of bandwidth.
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    clearTimeout(this.warmTimer);
    this.watcher?.disconnect();
    document.removeEventListener('visibilitychange', this.onPageVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  get slides() {
    return [...this.querySelectorAll('[data-ia-slide]')];
  }

  get auto() {
    return this.hasAttribute('data-auto') && !this.calm.matches;
  }

  get imageMs() {
    return Number(this.dataset.imageMs) || 5000;
  }

  /** The way slides travel at this width: phones have their own setting. */
  get axis() {
    const dir = this.phone.matches && this.dataset.directionM ? this.dataset.directionM : this.dataset.direction;
    return dir === 'horizontal' ? 'X' : 'Y';
  }

  /** The film the showing slide plays at this width, if it is a video slide. */
  currentVideo() {
    const frame = this.slides[this.index]?.querySelector('ia-video');
    if (!frame) return null;
    return frame.current ?? frame.querySelector('video');
  }

  /** Anything in `holds` stops the clock; a moving-on that falls due meanwhile waits for it. */
  hold(reason, on) {
    if (on) this.holds.add(reason);
    else this.holds.delete(reason);
    this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    clearTimeout(this.warmTimer);
    this.timer = null;
    if (!this.auto || this.slides.length < 2) return;

    if (this.holds.size) {
      // An image's clock resumes where it stopped rather than from the top.
      if (this.deadline) this.remaining = Math.max(0, this.deadline - performance.now());
      this.deadline = null;
      return;
    }
    if (this.pendingNext) {
      this.pendingNext = false;
      this.next();
      return;
    }

    const slide = this.slides[this.index];
    if (slide.dataset.kind === 'video' && slide.querySelector('video')) {
      // A film moves on from its 'ended' event. This only checks back on one
      // that never started (autoplay refused on a phone in low-power mode) or
      // stalled, so the slideshow cannot stop for good.
      this.timer = setTimeout(this.checkFilm, Number(slide.dataset.ms) || this.imageMs);
      return;
    }

    const wait = this.remaining ?? (Number(slide.dataset.ms) || this.imageMs);
    this.remaining = null;
    this.deadline = performance.now() + wait;
    this.timer = setTimeout(() => this.next(), wait);
    // A film warms its successor from its own clock (onTimeUpdate); an image
    // banner from this one.
    const index = this.index;
    this.warmTimer = setTimeout(() => this.warm(index + 1), Math.max(0, wait - IaFilm.WARM_LEAD_MS));
  }

  /** The showing film is WARM_LEAD_MS from its end: fetch what comes after it. */
  onTimeUpdate = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !this.auto) return;
    const slide = video.closest('[data-ia-slide]');
    if (!slide || slide !== this.slides[this.index]) return;
    const left = video.duration - video.currentTime;
    if (Number.isFinite(left) && left * 1000 <= IaFilm.WARM_LEAD_MS) this.warm(this.index + 1);
  };

  checkFilm = () => {
    this.timer = null;
    const video = this.currentVideo();
    if (!video || video.ended) return this.next();

    const moved = video.currentTime !== this.lastTime;
    this.lastTime = video.currentTime;
    this.stalls = moved ? 0 : (this.stalls || 0) + 1;
    // Skipped only when it clearly cannot play: it has the data yet stays
    // paused (autoplay refused), or nothing has moved for about 20 seconds.
    // A film still arriving over a slow phone connection is waited for.
    const refused = video.paused && !moved && video.readyState >= 3;
    if (refused || this.stalls >= 5) return this.next();
    this.timer = setTimeout(this.checkFilm, 4000);
  };

  onEnded = (event) => {
    const slide = event.target instanceof Element ? event.target.closest('[data-ia-slide]') : null;
    if (!slide || slide !== this.slides[this.index] || !this.auto) return;
    if (this.holds.size) {
      this.pendingNext = true;
      return;
    }
    this.next();
  };

  next() {
    this.go(this.index + 1, 1);
  }

  /**
   * Show slide `to`. `dir` is 1 for forward (the next one rises from below, or
   * comes in from the right) and -1 for back; it is worked out when omitted.
   */
  go(to, dir) {
    const slides = this.slides;
    const n = slides.length;
    if (n < 2) return;
    to = ((to % n) + n) % n;
    if (to === this.index) {
      this.schedule();
      return;
    }
    dir = dir || (to > this.index ? 1 : -1);

    // A transition still running is finished first, so slides never pile up.
    this.settle?.();

    const from = slides[this.index];
    const next = slides[to];
    this.index = to;
    this.remaining = null;
    this.deadline = null;
    this.pendingNext = false;
    this.lastTime = undefined;
    this.stalls = 0;

    from.classList.remove('is-active');
    from.classList.add('is-leaving');
    this.mark(from, false);
    next.classList.add('is-active');
    this.mark(next, true);

    // A film always starts from its beginning when its slide comes round.
    const film = next.querySelector('ia-video');
    const video = film?.current ?? film?.querySelector('video');
    if (video && video.currentTime > 0) {
      try {
        video.currentTime = 0;
      } catch {
        /* not seekable yet: it is at the start anyway */
      }
    }
    if (film?.wanted) film.play?.();

    const done = () => {
      from.classList.remove('is-leaving');
      from.querySelector('ia-video')?.pause?.();
      this.settle = null;
    };

    const speed = this.calm.matches ? 0 : Number(this.dataset.speed) || 700;
    if (speed && next.animate) {
      const axis = this.axis;
      const easing = 'cubic-bezier(0.65, 0, 0.35, 1)';
      const enter = next.animate(
        [{ transform: `translate${axis}(${dir * 100}%)` }, { transform: 'none' }],
        { duration: speed, easing }
      );
      const leave = from.animate(
        [{ transform: 'none' }, { transform: `translate${axis}(${-dir * 100}%)` }],
        { duration: speed, easing, fill: 'forwards' }
      );
      const settle = () => {
        if (this.settle !== settle) return;
        enter.finish();
        leave.cancel();
        done();
      };
      this.settle = settle;
      leave.finished.then(settle, () => {});
    } else {
      done();
    }

    this.paintDots();
    // The slide now showing (a dot or swipe can jump past the warmed one),
    // then the one after it in the series, so it is in hand well before due.
    this.warm(to);
    this.warm(to + dir);
    this.schedule();
  }

  mark(slide, on) {
    slide.toggleAttribute('inert', !on);
    if (on) slide.removeAttribute('aria-hidden');
    else slide.setAttribute('aria-hidden', 'true');
  }

  paintDots() {
    for (const dot of this.querySelectorAll('[data-ia-film-dot]')) {
      const on = Number(dot.dataset.iaFilmDot) === this.index;
      dot.classList.toggle('is-active', on);
      if (on) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }

  /**
   * Fetch a slide's banner before it slides in: its picture, or its film (only
   * the cut this width shows). Once per slide; looping round is free.
   */
  warm(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const slide = slides[((index % slides.length) + slides.length) % slides.length];
    if (slide.dataset.iaWarm) return;
    slide.dataset.iaWarm = '1';
    for (const img of slide.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
    const film = slide.querySelector('ia-video');
    const video = film?.current;
    if (video && video.preload === 'none' && video.readyState === 0) {
      video.preload = 'auto';
      video.load();
    }
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const dot = target.closest('[data-ia-film-dot]');
    if (dot) return this.go(Number(dot.dataset.iaFilmDot));
    if (target.closest('[data-ia-next]')) return this.go(this.index + 1, 1);
    if (target.closest('[data-ia-prev]')) return this.go(this.index - 1, -1);
  };

  onClickAfterSwipe = (event) => {
    if (this.swipedAt && event.timeStamp - this.swipedAt < 500) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  /**
   * Scrolling over the slideshow (data-scroll) steps through it instead of the
   * page: down for the next slide, up for the previous one. At the last slide
   * going down, or the first going up, the page scrolls as usual — and only
   * when the slideshow is mostly on screen, so arriving at it from above or
   * below never gets caught halfway.
   */
  canStep(dir) {
    if (!this.hasAttribute('data-scroll')) return false;
    const n = this.slides.length;
    if (n < 2) return false;
    if (dir > 0 ? this.index >= n - 1 : this.index <= 0) return false;
    const rect = this.getBoundingClientRect();
    const shown = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    return shown >= Math.min(rect.height, window.innerHeight) * 0.6;
  }

  onWheel = (event) => {
    if (event.ctrlKey) return; // pinch-zoom on a trackpad
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
    if (!delta) return;
    const dir = delta > 0 ? 1 : -1;

    // Right after a step, the rest of that gesture — a trackpad's momentum,
    // a wheel still spinning — is swallowed: no second step, no page scroll.
    // Each event extends the lock, so one flick is one slide.
    if (event.timeStamp < (this.wheelLock || 0)) {
      event.preventDefault();
      this.wheelLock = Math.max(this.wheelLock, event.timeStamp + 160);
      return;
    }
    if (!this.canStep(dir)) {
      this.wheelSum = 0;
      return;
    }
    event.preventDefault();

    // A few small trackpad deltas add up to one deliberate turn.
    if (this.wheelDir !== dir || event.timeStamp - (this.wheelAt || 0) > 250) this.wheelSum = 0;
    this.wheelDir = dir;
    this.wheelAt = event.timeStamp;
    this.wheelSum = (this.wheelSum || 0) + Math.abs(delta) * (event.deltaMode === 1 ? 16 : 1);
    if (this.wheelSum < 40) return;

    this.wheelSum = 0;
    const speed = this.calm.matches ? 0 : Number(this.dataset.speed) || 700;
    this.wheelLock = event.timeStamp + speed + 250;
    this.go(this.index + dir, dir);
  };

  onSwipeStart = (event) => {
    this.swipe = event.touches.length === 1
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY, at: event.timeStamp, mode: null }
      : null;
  };

  // The first clear movement decides what a touch is: sideways (always the
  // slideshow's), vertical while there is a slide to go to (the slideshow's,
  // and the page is held still), or vertical at either end (the page's).
  // Slides that travel sideways leave every vertical touch to the page.
  onSwipeMove = (event) => {
    const start = this.swipe;
    if (!start || event.touches.length !== 1) return;
    const dx = event.touches[0].clientX - start.x;
    const dy = event.touches[0].clientY - start.y;
    if (!start.mode) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        start.mode = event.cancelable && this.axis === 'Y' && this.canStep(dy < 0 ? 1 : -1) ? 'vertical' : 'page';
      } else {
        start.mode = 'sideways';
      }
    }
    if (start.mode === 'vertical') event.preventDefault();
  };

  onSwipeEnd = (event) => {
    const start = this.swipe;
    this.swipe = null;
    if (!start || event.changedTouches.length !== 1) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    if (start.mode === 'vertical') {
      if (Math.abs(dy) < 30) return;
      this.swipedAt = event.timeStamp;
      // Finger up is scrolling down: the next slide.
      this.go(this.index + (dy < 0 ? 1 : -1), dy < 0 ? 1 : -1);
      return;
    }
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5 || event.timeStamp - start.at > 800) return;
    this.swipedAt = event.timeStamp;
    this.go(this.index + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
  };

  onPageVisibility = () => this.hold('hidden', document.hidden);

  // Theme editor: selecting a slide's block shows it and keeps it there.
  onBlockSelect = (event) => {
    const slide = event.target instanceof Element ? event.target.closest('[data-ia-slide]') : null;
    if (!slide || !this.contains(slide)) return;
    this.holds.add('editor');
    this.go(this.slides.indexOf(slide));
  };

  onBlockDeselect = (event) => {
    if (event.target instanceof Element && this.contains(event.target)) this.hold('editor', false);
  };
}

/**
 * Shopify's localization form, submitted on change.
 *
 * The platform's own form posts a country code and reloads in that market —
 * this only saves the visitor hunting for a submit button. Without JavaScript
 * the form keeps the button in its <noscript>.
 */
document.addEventListener('change', (event) => {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement)) return;
  const form = select.closest('[data-ia-localization]');
  if (form) form.submit();
});

/**
 * A <details> popup closes when the page is clicked elsewhere, or on Escape.
 *
 * <details> handles opening, keyboard focus and the toggle state on its own;
 * this only adds the two behaviours a popup needs that a disclosure does not.
 */
document.addEventListener('click', (event) => {
  for (const pop of document.querySelectorAll('details[data-ia-pop][open]')) {
    if (!pop.contains(event.target)) pop.open = false;
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  for (const pop of document.querySelectorAll('details[data-ia-pop][open]')) {
    pop.open = false;
    pop.querySelector('summary')?.focus();
  }
});

/**
 * The buy box.
 *
 * Every variant ships with the page as JSON, so picking an option is a lookup
 * rather than a request: the price, the saving, the SKU, the stock line, the
 * button and the URL all move together and nothing is fetched.
 *
 * Option buttons are disabled when no variant exists for that combination, and
 * each one carries the stock note for the variant it would select — read off
 * the inventory rather than typed in by hand.
 */
class IaProduct extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('[data-ia-variants]');
    if (!payload) return;

    try {
      this.variants = JSON.parse(payload.textContent);
    } catch {
      return;
    }
    if (!this.variants.length) return;

    this.groups = [...this.querySelectorAll('[data-ia-option]')];
    this.selected = this.variants.find((variant) => variant.available) || this.variants[0];

    // Whatever the markup rendered as pressed is the starting point; the first
    // available variant only fills in the gaps.
    this.choice = this.groups.map((group, index) => {
      const pressed = group.querySelector('[aria-pressed="true"]');
      return pressed ? pressed.dataset.iaValue : this.selected.options[index];
    });

    this.lowAt = Number(this.dataset.lowStock || 10);
    this.addEventListener('click', this.onClick);
    this.addEventListener('click', this.onThumb);
    this.addEventListener('click', this.onZoom);
    this.addEventListener('keydown', this.onZoomKey);
    // Phones show one picture at a time; a sideways swipe steps it like the arrows.
    this.addEventListener('touchstart', this.onSwipeStart, { passive: true });
    this.addEventListener('touchend', this.onSwipeEnd, { passive: true });
    this.watchGallery();
    this.watchBar();

    const qty = this.querySelector('[data-ia-qty-input]');
    if (qty) {
      this.addEventListener('click', this.onQuantity);
      // Typing 9 into the box has to be clamped just as pressing + is.
      qty.addEventListener('change', () => this.setQuantity(qty.value));
    }

    this.apply({ push: false });
  }

  /**
   * Give the mosaic the height of the rows the merchant asked for.
   *
   * It cannot be worked out in CSS: a row's height comes from the column
   * width and the shape of the frames, and the rows are not all the same
   * height. So it is measured — once the pictures have loaded, and again
   * whenever the column changes width.
   */
  watchGallery() {
    const gallery = this.querySelector('[data-ia-gallery]');
    if (!gallery) return;

    // The shape of each position, captured before anything moves. Re-applied
    // by index after every reorder, which is the only way the mosaic keeps its
    // shape when a picture jumps the queue.
    this.slots = [...gallery.children].map((cell) => ({
      cls: cell.className,
      span: cell.style.getPropertyValue('--ia-span'),
    }));

    this.scroller = this.querySelector('[data-ia-scroller].ia-pdp__scroller--on');
    if (!this.scroller) return;

    const measure = () => this.sizeGallery();
    // The parent, never the window itself: setting the window's height would
    // wake the observer that set it.
    const column = this.scroller.parentElement;
    if (column && 'ResizeObserver' in window) {
      this.galleryObserver = new ResizeObserver(measure);
      this.galleryObserver.observe(column);
    }

    for (const img of gallery.querySelectorAll('img')) {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    }
    measure();
  }

  sizeGallery() {
    const scroller = this.scroller;
    const gallery = scroller?.querySelector('[data-ia-gallery]');
    if (!gallery) return;

    // The desktop mosaic counts rows of two and three; a phone counts single
    // pictures. The observer re-runs this when the column changes width, so
    // crossing the breakpoint picks up the other number on its own.
    const wide = window.matchMedia('(min-width: 990px)').matches;
    const rows = Number((wide ? scroller.dataset.iaRows : scroller.dataset.iaRowsMobile) || 0);
    const cells = [...gallery.children].filter((cell) => cell.offsetParent !== null);
    if (!rows || !cells.length) return;

    // Cells sharing a top edge are a row; the cap is the bottom of the Nth.
    const tops = [...new Set(cells.map((cell) => cell.offsetTop))].sort((a, b) => a - b);
    if (tops.length <= rows) {
      scroller.style.removeProperty('--ia-pdp-scroll-h');
      return;
    }

    const lastTop = tops[rows - 1];
    const bottom = Math.max(
      ...cells.filter((cell) => cell.offsetTop === lastTop).map((cell) => cell.offsetTop + cell.offsetHeight)
    );
    scroller.style.setProperty('--ia-pdp-scroll-h', `${Math.round(bottom - tops[0])}px`);
  }

  /**
   * The buy bar at the foot of the page.
   *
   * It appears once the real buy box has left the screen and goes away again
   * when it comes back, which an observer answers exactly and a scroll
   * listener only approximates.
   */
  /*
   * The theme editor swaps the whole section on every setting change. Without
   * this the old copy's observers stayed alive, watching nodes no longer on
   * the page, and each toggle stacked another set of them.
   */
  disconnectedCallback() {
    this.barObserver?.disconnect();
    this.galleryObserver?.disconnect();
  }

  watchBar() {
    this.bar = this.querySelector('[data-ia-bar]');
    const box = this.querySelector('[data-ia-buybox]');
    if (!this.bar || !box || !('IntersectionObserver' in window)) return;

    this.bar.hidden = false;
    this.barNote = this.bar.querySelector('[data-ia-bar-note]');

    this.barObserver = new IntersectionObserver(
      ([entry]) => this.bar.classList.toggle('is-on', !entry.isIntersecting),
      { rootMargin: '0px 0px -120px 0px' }
    ).observe(box);

    // Press the real controls rather than keeping a second form in step.
    this.bar.querySelector('[data-ia-bar-atc]')?.addEventListener('click', () => {
      this.querySelector('[data-ia-atc]')?.click();
    });
    this.bar.querySelector('[data-ia-bar-buy]')?.addEventListener('click', () => {
      this.querySelector('[data-ia-buy-now]')?.click();
    });

    this.paintBar();
  }

  /** The variant line in the bar, and whether its buttons can be pressed. */
  paintBar() {
    if (!this.bar) return;

    const atc = this.querySelector('[data-ia-atc]');
    for (const button of this.bar.querySelectorAll('button')) {
      button.disabled = !!atc?.disabled;
    }

    if (!this.barNote) return;

    // Every option the visitor has chosen, not just the first: a color and a
    // size are both worth repeating in a bar that has replaced the buy box.
    const parts = (this.choice || []).filter(Boolean);

    // The urgency line, which paintStock has just written for this variant.
    // Deliberately NOT [data-ia-note]: that lives inside each swatch, so the
    // first swatch's label was being reported as the selected variant's stock.
    const urgency = this.querySelector('[data-ia-urgency]');
    const urgent = this.querySelector('[data-ia-urgency-text]')?.textContent.trim();
    if (urgency && !urgency.hidden && urgent) {
      parts.push(urgent);
    } else if (this.selected.available === false) {
      parts.push(this.dataset.soldOutLabel || 'Sold out');
    } else if (this.dataset.inStockNote) {
      parts.push(this.dataset.inStockNote);
    }

    this.barNote.textContent = parts.join(' · ');
  }

  onSwipeStart = (event) => {
    const inGallery = event.target instanceof Element && event.target.closest('[data-ia-gallery]');
    this.swipe = inGallery && event.touches.length === 1
      ? { x: event.touches[0].clientX, y: event.touches[0].clientY, at: event.timeStamp }
      : null;
  };

  onSwipeEnd = (event) => {
    const start = this.swipe;
    this.swipe = null;
    if (!start || event.changedTouches.length !== 1) return;
    const dx = event.changedTouches[0].clientX - start.x;
    const dy = event.changedTouches[0].clientY - start.y;
    // Deliberate and mostly sideways: a page scroll that drifts stays a scroll.
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5 || event.timeStamp - start.at > 800) return;
    this.swipedAt = event.timeStamp;
    this.stepMedia(dx < 0 ? 1 : -1);
  };

  onZoom = (event) => {
    const frame = event.target instanceof Element ? event.target.closest('[data-ia-zoom-open]') : null;
    if (!frame) return;
    // The tap a swipe can leave behind must not open the zoom.
    if (this.swipedAt && event.timeStamp - this.swipedAt < 500) return;
    this.querySelector('ia-zoom')?.open(frame.dataset.iaZoomOpen);
  };

  onZoomKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const frame = event.target instanceof Element ? event.target.closest('[data-ia-zoom-open]') : null;
    if (!frame) return;
    event.preventDefault();
    this.querySelector('ia-zoom')?.open(frame.dataset.iaZoomOpen);
  };

  onThumb = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('[data-ia-thumb]') : null;
    if (!thumb) return;
    this.promote(this.querySelector(`[data-ia-media="${thumb.dataset.iaThumb}"]`));
  };

  onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ia-value]') : null;
    if (!button || button.disabled) return;

    const index = Number(button.dataset.iaIndex);
    this.choice[index] = button.dataset.iaValue;
    this.apply({ push: true });
  };

  onQuantity = (event) => {
    const step = event.target instanceof Element ? event.target.closest('[data-ia-qty]') : null;
    if (!step) return;

    const input = this.querySelector('[data-ia-qty-input]');
    if (!input) return;
    this.setQuantity((Number(input.value) || 1) + Number(step.dataset.iaQty));
  };

  /**
   * How many of the selected variant may actually be bought.
   *
   * null means no ceiling: either the variant is not tracked, or its policy is
   * "continue", which is Shopify's way of saying sell past zero on purpose.
   */
  get stockCap() {
    const v = this.selected;
    if (!v || !v.tracked || v.policy === 'continue') return null;
    return typeof v.left === 'number' && v.left > 0 ? v.left : 1;
  }

  /** One way in and out of the quantity box, so every path is clamped. */
  setQuantity(value) {
    const input = this.querySelector('[data-ia-qty-input]');
    if (!input) return;

    const cap = this.stockCap;
    let next = Math.max(1, Math.round(Number(value) || 1));
    if (cap !== null) next = Math.min(next, cap);
    input.value = next;

    if (cap === null) input.removeAttribute('max');
    else input.max = cap;

    // The step that would go past the ceiling is the one to switch off.
    const up = this.querySelector('[data-ia-qty="1"]');
    const down = this.querySelector('[data-ia-qty="-1"]');
    if (up) up.disabled = cap !== null && next >= cap;
    if (down) down.disabled = next <= 1;
  }

  /** The variant matching a set of option values, or null. */
  match(values) {
    return (
      this.variants.find((variant) => values.every((value, i) => value == null || variant.options[i] === value)) || null
    );
  }

  apply({ push }) {
    const chosen = this.match(this.choice);
    if (chosen) this.selected = chosen;

    this.paintOptions();
    this.paintPrice();
    this.paintStock();
    this.paintButton();
    this.showMedia();
    // A different variant means a different ceiling, and the box may now be
    // holding more than the new one allows.
    this.setQuantity(this.querySelector('[data-ia-qty-input]')?.value ?? 1);

    // Last: it repeats what the others have just decided.
    this.paintBar();

    const field = this.querySelector('[data-ia-variant-id]');
    if (field) field.value = this.selected.id;

    if (push && this.selected) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.selected.id);
      window.history.replaceState({}, '', url);
    }
  }

  paintOptions() {
    this.groups.forEach((group, index) => {
      for (const button of group.querySelectorAll('[data-ia-value]')) {
        const value = button.dataset.iaValue;
        button.setAttribute('aria-pressed', String(this.choice[index] === value));

        // Would this value lead anywhere? Hold the other chosen values still
        // and ask whether any variant exists with this one swapped in.
        const probe = this.choice.slice();
        probe[index] = value;
        const candidate = this.match(probe) || this.match(probe.map((v, i) => (i === index ? v : null)));

        button.disabled = !candidate || !candidate.available;

        const note = button.querySelector('[data-ia-note]');
        if (!note) continue;

        if (!candidate || !candidate.available) {
          note.textContent = this.dataset.soldOutNote || 'Sold out';
          button.removeAttribute('data-ia-low');
        } else if (candidate.tracked && typeof candidate.left === 'number' && candidate.left > 0 && candidate.left <= this.lowAt) {
          note.textContent = `${candidate.left} left`;
          button.setAttribute('data-ia-low', '');
        } else {
          note.textContent = this.dataset.inStockNote || 'In stock';
          button.removeAttribute('data-ia-low');
        }
      }

      const label = group.querySelector('[data-ia-option-value]');
      if (label) label.textContent = this.choice[index] || '';
    });
  }

  /** Instalment partners: (price + markup) / count, rounded up to a whole unit. */
  paintInstalments(cents) {
    const box = this.querySelector('.ia-pdp__instals');
    if (!box || !Number.isFinite(cents)) return;
    const format = box.dataset.moneyFormat || '{{amount}}';
    for (const row of box.querySelectorAll('[data-ia-instal]')) {
      // A partner with a price limit hides above it, per variant.
      const max = Number(row.dataset.max) || 0;
      row.hidden = max > 0 && cents > max;
      // The line's amount and every amount in its details popup.
      const outs = row.querySelectorAll('[data-ia-instal-amount], [data-ia-instal-each]');
      if (!outs.length) continue;
      const count = Number(row.dataset.count) || 1;
      const markup = Number(row.dataset.markup) || 0;
      // A first-order discount the line shows the amount after (data-discount).
      const discount = Number(row.dataset.discount) || 0;
      const each = Math.ceil((cents * (1 + markup / 100) * (1 - discount / 100)) / count / 100);
      const whole = each.toLocaleString('en-US');
      const text = format
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount\s*\}\}/g, whole);
      for (const out of outs) iaSetMoney(out, text);
    }
  }

  paintPrice() {
    // querySelectorAll, not querySelector: the sticky bar carries its own
    // price and it has to say the same thing as the buy box.
    const prices = this.querySelectorAll('[data-ia-price]');
    const compare = this.querySelector('[data-ia-compare]');
    const save = this.querySelector('[data-ia-save]');
    const sku = this.querySelector('[data-ia-sku]');
    const atcPrice = this.querySelector('[data-ia-atc-price]');
    const v = this.selected;

    for (const node of prices) iaSetMoney(node, v.priceText);
    this.paintInstalments(Number(v.price));
    const off = this.querySelector('[data-ia-head-off]');
    if (off) {
      const price = Number(v.price);
      const was = Number(v.compare);
      const pct = was > price ? Math.floor(((was - price) * 100) / was) : 0;
      off.hidden = pct <= 0;
      if (pct > 0) off.textContent = (off.dataset.label || '[x]% OFF').replace('[x]', pct);
    }
    // Into the inner .money span when there is one, so the currency app keeps converting it.
    const atcAmount = atcPrice?.querySelector('[data-ia-atc-amount]');
    if (atcAmount) iaSetMoney(atcAmount, v.priceText);
    else if (atcPrice) atcPrice.textContent = `· ${v.priceText}`;
    if (sku && v.sku) sku.textContent = v.sku;

    const discounted = v.compare != null && Number(v.compare) > Number(v.price);
    if (compare) {
      iaSetMoney(compare, v.compareText || '');
      compare.hidden = !discounted;
    }
    if (save) {
      // Formatted by Shopify, per variant, rather than rebuilt here.
      save.hidden = !discounted;
      const saveAmount = save.querySelector('[data-ia-save-amount]');
      if (discounted && saveAmount) iaSetMoney(saveAmount, v.saveText);
      else if (discounted) save.textContent = `${this.dataset.saveLabel || 'Save'} ${v.saveText}`;
    }
  }

  paintStock() {
    const box = this.querySelector('[data-ia-urgency]');
    if (!box) return;

    const v = this.selected;
    const low = v.tracked && typeof v.left === 'number' && v.left > 0 && v.left <= this.lowAt;
    box.hidden = !low;
    if (!low) return;

    const text = box.querySelector('[data-ia-urgency-text]');
    const fill = box.querySelector('[data-ia-urgency-fill]');
    if (text) text.textContent = (this.dataset.urgencyText || 'Only {n} left').replace('{n}', v.left);
    if (fill) fill.style.width = `${Math.max(6, Math.round((v.left / this.lowAt) * 100))}%`;
  }

  paintButton() {
    // Buy it now (buy box and sticky bar) only for a variant that can be bought.
    for (const buyNow of this.querySelectorAll('[data-ia-buy-now], [data-ia-bar-buy]')) {
      buyNow.hidden = !this.selected.available;
    }

    const button = this.querySelector('[data-ia-atc]');
    if (!button) return;

    const label = button.querySelector('[data-ia-atc-text]');
    button.disabled = !this.selected.available;
    if (label) {
      label.textContent = this.selected.available
        ? this.dataset.atcLabel || label.textContent
        : this.dataset.soldOutLabel || 'Sold out';
    }
  }

  /**
   * Show the variant's own photograph.
   *
   * Where the gallery is a scrolling window, nothing is moved: the window
   * scrolls to the picture. Rearranging a mosaic under someone who has just
   * clicked a color is the jarring part, and the large retailers do not do
   * it. Only when the picture is genuinely out of reach — past a row cap, with
   * no window to scroll — is it brought to the front instead.
   */
  showMedia() {
    if (!this.selected.mediaId) return;
    const cell = this.querySelector(`[data-ia-media="${this.selected.mediaId}"]`);
    if (!cell) return;

    const gallery = this.querySelector('[data-ia-gallery]');
    if (this.scroller && gallery && cell.offsetParent !== null) {
      this.scroller.scrollTo({
        top: Math.max(0, cell.offsetTop - gallery.offsetTop),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      });
      this.markThumb(cell.dataset.iaMedia);
      return;
    }

    this.promote(cell);
  }

  /**
   * Move a gallery cell into the first frame.
   *
   * The classes and the span are swapped rather than the nodes, because the
   * mosaic's shape belongs to the position and not to the picture in it — and
   * so does whether the position is one of the hidden ones past the row cap.
   * That is what lets a thumbnail reach an image the cap left out: the picture
   * it displaces simply inherits the hidden slot and the page keeps its height.
   */
  promote(cell) {
    const gallery = this.querySelector('[data-ia-gallery]');
    if (!cell || !gallery) return;

    if (cell !== gallery.firstElementChild) {
      gallery.insertBefore(cell, gallery.firstElementChild);
      // Every position takes back its own shape. Trading class names between
      // just two cells was the bug: the displaced picture does not land where
      // the promoted one came from, it lands one place along, so a first row
      // of two heroes turned into a hero and a third-width cell.
      this.applySlots(gallery);

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        cell.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      }
    }

    this.markThumb(cell.dataset.iaMedia);
    this.sizeGallery();
  }

  /**
   * Main-picture arrows: bring the next / previous picture (in the product's
   * own order) to the front. The cells are laid out as a rotation from that
   * picture, so the mosaic keeps its sequence instead of shuffling.
   */
  mediaNeighbour(dir) {
    const gallery = this.querySelector('[data-ia-gallery]');
    if (!gallery || gallery.children.length < 2) return null;
    const index = (cell) => Number(cell.dataset.iaIndex) || 0;
    const ordered = [...gallery.children].sort((a, b) => index(a) - index(b));
    const n = ordered.length;
    const pos = ordered.indexOf(gallery.firstElementChild);
    return { gallery, ordered, target: ordered[(pos + dir + n) % n] };
  }

  /** Start fetching the picture an arrow would bring in (it is lazy and hidden). */
  warmMedia(dir) {
    const img = this.mediaNeighbour(dir)?.target.querySelector('img[loading="lazy"]');
    if (img) img.loading = 'eager';
  }

  stepMedia(dir) {
    const found = this.mediaNeighbour(dir);
    if (!found) return;
    const { gallery, ordered, target } = found;
    const start = ordered.indexOf(target);
    gallery.append(...ordered.slice(start), ...ordered.slice(0, start));
    this.applySlots(gallery);
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      target.animate?.([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    }
    this.markThumb(target.dataset.iaMedia);
    this.sizeGallery();
    this.warmMedia(dir);

    // Keep the lit thumbnail in view in its strip (never scrolls the page).
    const thumb = this.querySelector('[data-ia-thumb].is-active');
    const strip = thumb?.parentElement;
    if (strip) {
      const t = thumb.getBoundingClientRect();
      const r = strip.getBoundingClientRect();
      if (getComputedStyle(strip).flexDirection === 'column') {
        strip.scrollTo({ top: strip.scrollTop + t.top - r.top - (r.height - t.height) / 2, behavior: 'smooth' });
      } else {
        strip.scrollTo({ left: strip.scrollLeft + t.left - r.left - (r.width - t.width) / 2, behavior: 'smooth' });
      }
    }
  }

  /** Give positions 0..n back the shapes they were rendered with. */
  applySlots(gallery) {
    if (!this.slots) return;
    [...gallery.children].forEach((cell, index) => {
      const slot = this.slots[index];
      if (!slot) return;
      cell.className = slot.cls;
      if (slot.span) cell.style.setProperty('--ia-span', slot.span);
    });
  }

  /** Whichever image is in the first frame is the one the strip points at. */
  markThumb(id) {
    this.querySelectorAll('[data-ia-thumb]').forEach((thumb) => {
      const on = thumb.dataset.iaThumb === id;
      thumb.classList.toggle('is-active', on);
      if (on) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

/**
 * The "complete the look" set.
 *
 * Ticking a pair re-reckons the total; the button puts everything ticked in
 * the bag in one request. A theme cannot discount a cart by itself, so when a
 * code is configured the browser is sent to /discount/CODE afterwards, which
 * is how Shopify applies one.
 *
 * A gift box set (data-gift-for) ties every box to the product on the page
 * with a hidden _gift_for line property, and adds that product too when it
 * is not in the bag yet: a gift box is never sold on its own. iaGiftGuard
 * below takes the boxes out again when the product leaves the bag.
 */
class IaBundle extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-ia-bun-item]')];
    this.button = this.querySelector('[data-ia-bun-add]');
    if (!this.items.length || !this.button) return;

    this.totalEl = this.querySelector('[data-ia-bun-total]');
    this.wasEl = this.querySelector('[data-ia-bun-was]');
    this.saveEl = this.querySelector('[data-ia-bun-save]');
    this.countEl = this.querySelector('[data-ia-bun-count]');
    this.label = this.querySelector('[data-ia-bun-add-text]');
    this.percent = Number(this.dataset.percent || 0);
    this.format = this.dataset.moneyFormat || '${{amount}}';

    this.addEventListener('change', this.onChange);
    this.button.addEventListener('click', this.onAdd);

    // Gift boxes: one per item. What fits depends on the bag and, before the
    // product is in it, on the quantity picked in the buy box.
    this.giftFor = this.dataset.giftFor;
    if (this.giftFor) {
      this.limitEl = this.querySelector('[data-ia-bun-limit]');
      this.giftRoom = { parentQty: 0, giftQty: 0 };
      this.form = document.querySelector(`product-form-component[data-product-id="${this.giftFor}"]`);
      this.form?.addEventListener('click', this.onBuyBoxQty);
      this.form?.addEventListener('input', this.onBuyBoxQty);
      this.form?.addEventListener('change', this.onBuyBoxQty);
      document.addEventListener('cart:update', this.onCartUpdate);
      this.readBag();
    }
    this.paint();
  }

  disconnectedCallback() {
    if (!this.giftFor) return;
    this.form?.removeEventListener('click', this.onBuyBoxQty);
    this.form?.removeEventListener('input', this.onBuyBoxQty);
    this.form?.removeEventListener('change', this.onBuyBoxQty);
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  // The buy box steps its quantity in its own click handler: read it after.
  onBuyBoxQty = () => requestAnimationFrame(() => this.paint());

  onCartUpdate = () => this.readBag();

  get pageQty() {
    const input = this.form?.querySelector('input[name="quantity"]');
    return Math.max(1, parseInt(input?.value, 10) || 1);
  }

  /** How many of the product, and of its gift boxes, the bag holds. */
  async readBag() {
    try {
      const cartUrl = Theme?.routes?.cart_url || '/cart';
      const cart = await (await fetch(`${cartUrl}.js`)).json();
      this.giftRoom = iaGiftCount(cart, this.giftFor);
    } catch (error) {
      // Unknown: the button stays as it is, and the cart guard still holds.
    }
    this.paint();
  }

  /**
   * Gift boxes that can still go in: one per item of the product. Before the
   * product is in the bag, the buy box quantity is what will go in with them.
   */
  get giftAllowance() {
    const { parentQty, giftQty } = this.giftRoom;
    const items = parentQty > 0 ? parentQty : this.pageQty;
    return Math.max(0, items - giftQty);
  }

  onChange = (event) => {
    const box = event.target.closest('[data-ia-bun-item]');
    if (!box) return;
    box.closest('.ia-bun__row')?.classList.toggle('is-on', box.checked);
    this.paint();
  };

  get picked() {
    return this.items.filter((box) => box.checked);
  }

  /**
   * Shopify's money format is a template with its own placeholders, and the
   * shop's own one is the only format the rest of the page is using.
   */
  money(cents) {
    // Whole amounts drop the decimals, matching the money_without_trailing_zeros
    // filter the Liquid side uses — otherwise a bundle total reads 4,500.00
    // beside a buy-box price of 4,500.
    const decimals = cents % 100 === 0 ? 0 : 2;
    const amount = (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    const whole = Math.round(cents / 100).toLocaleString('en-US');
    return this.format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  paint() {
    const picked = this.picked;
    const gross = picked.reduce((sum, box) => sum + Number(box.dataset.price || 0), 0);
    const net = this.percent > 0 ? Math.round(gross * (100 - this.percent) / 100) : gross;

    // The total and count only mean something once a pair is ticked.
    this.toggleAttribute('data-empty', picked.length === 0);
    if (this.countEl) this.countEl.textContent = picked.length;
    iaSetMoney(this.totalEl, this.money(net));
    iaSetMoney(this.wasEl, this.money(gross));
    if (this.saveEl) this.saveEl.innerHTML = `You save <span class="money">${this.money(gross - net)}</span>`;

    // The product on the page is not part of the set; it has its own button
    // above. So one pair is still a set worth adding.
    this.button.disabled = picked.length < 1;

    if (this.giftFor) {
      const allowed = this.giftAllowance;
      const over = picked.length > allowed;
      if (over) this.button.disabled = true;
      if (this.limitEl) {
        // What fits, and what to do about it: untick the rest, or buy more.
        const note = (allowed > 0 ? this.dataset.giftLimitNote : this.dataset.giftFullNote) || '';
        const filled = note
          .replaceAll('[n]', allowed)
          .replaceAll('[boxes]', allowed === 1 ? 'gift box' : 'gift boxes')
          .replaceAll('[extra]', picked.length - allowed);
        this.limitEl.textContent = over ? filled : '';
        this.limitEl.hidden = !over;
      }
    }
  }

  onAdd = async () => {
    const extras = this.picked.filter((box) => !box.disabled);
    if (!extras.length || this.busy) return;
    if (this.giftFor && extras.length > this.giftAllowance) return this.paint();

    this.busy = true;
    const held = this.label?.textContent;
    this.button.disabled = true;
    if (this.label) this.label.textContent = Theme?.translations?.adding || 'Adding…';

    try {
      // A code can only be applied by visiting Shopify's discount route. Asking
      // for it in the background sets it on the session and leaves the page
      // where it is, so the cart can still be shown rather than navigated to.
      const code = this.dataset.discount;
      if (code) {
        await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
      }

      const giftFor = this.dataset.giftFor;
      const items = extras.map((box) => ({
        id: Number(box.dataset.id),
        quantity: 1,
        ...(giftFor ? { properties: { _gift_for: giftFor } } : {}),
      }));
      if (giftFor) {
        const parent = await this.parentLine(giftFor);
        if (parent) items.unshift(parent);
      }

      const cartUrl = Theme?.routes?.cart_url || '/cart';
      const base = Theme?.routes?.cart_add_url || '/cart/add';
      const addUrl = base.endsWith('.js') ? base : `${base}.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({ items }),
      });
      const added = await response.json();
      if (!response.ok) throw new Error(added.description || added.message || response.statusText);

      // Everything that listens for a cart change wants the cart itself, not
      // just the lines that were added.
      const cart = await (await fetch(`${cartUrl}.js`)).json();
      if (this.giftFor) {
        this.giftRoom = iaGiftCount(cart, this.giftFor);
        // Added: clear the ticks, so the next choice starts from what fits.
        extras.forEach((box) => {
          box.checked = false;
          box.closest('.ia-bun__row')?.classList.remove('is-on');
        });
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: this.id || 'ia-bundle',
            data: { source: 'ia-bundle', itemCount: cart.item_count },
          },
        })
      );

      if (Theme?.translations?.added && this.label) {
        this.label.textContent = Theme.translations.added;
        setTimeout(() => { this.label.textContent = held; }, 1800);
      } else if (this.label) {
        this.label.textContent = held;
      }

      // The drawer opens itself when it carries auto-open; otherwise ask it.
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && !drawer.hasAttribute('auto-open')) drawer.open?.();
    } catch (error) {
      if (window.console) console.warn('[ia-bundle] could not add the set:', error);
      if (this.label) this.label.textContent = held;
    } finally {
      this.busy = false;
      this.paint();
    }
  };

  /**
   * The line for the product a gift box is sold with, when the bag does not
   * hold it yet: the variant picked in the buy box, one of it.
   */
  async parentLine(productId) {
    const cartUrl = Theme?.routes?.cart_url || '/cart';
    const cart = await (await fetch(`${cartUrl}.js`)).json();
    if (cart.items.some((item) => String(item.product_id) === String(productId) && !item.properties?._gift_for)) {
      return null;
    }
    const input = document.querySelector(
      `product-form-component[data-product-id="${productId}"] [data-ia-variant-id]`
    );
    return input?.value ? { id: Number(input.value), quantity: this.pageQty } : null;
  }
}

if (!customElements.get('ia-bundle')) customElements.define('ia-bundle', IaBundle);

/**
 * How many of a product the bag holds (its own lines) and how many gift boxes
 * are tied to it (_gift_for).
 */
function iaGiftCount(cart, productId) {
  const id = String(productId);
  let parentQty = 0;
  let giftQty = 0;
  for (const item of cart.items || []) {
    const parent = item.properties?._gift_for;
    if (parent) {
      if (String(parent) === id) giftQty += item.quantity;
    } else if (String(item.product_id) === id) {
      parentQty += item.quantity;
    }
  }
  return { parentQty, giftQty };
}

/**
 * Keeps gift boxes from being bought on their own. Whenever the cart changes
 * (and once per page, for a change made without JS), any gift box whose
 * product is no longer in the bag is taken out, as is a product of a gift
 * box collection (Theme.giftBoxIds) that went in without one. Gift boxes
 * never outnumber their product either: when the product goes down, the
 * newest boxes give way first.
 *
 * The cart drawer and cart page redraw themselves off the cart:update this
 * sends. Its own event is ignored, so it never chases itself.
 */
const iaGiftGuard = {
  timer: 0,

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.sweep(), 150);
  },

  async sweep() {
    const cartUrl = Theme?.routes?.cart_url || '/cart';
    const giftIds = new Set((Theme?.giftBoxIds || []).map(String));
    try {
      const cart = await (await fetch(`${cartUrl}.js`)).json();
      // Room left for gift boxes, per product: one per item.
      const room = new Map();
      for (const item of cart.items) {
        if (item.properties?._gift_for) continue;
        const id = String(item.product_id);
        room.set(id, (room.get(id) || 0) + item.quantity);
      }

      const updates = {};
      // Oldest lines first (the cart lists the newest on top), so what was
      // added last is what gives way.
      for (const item of [...cart.items].reverse()) {
        const parent = item.properties?._gift_for;
        if (!parent) {
          if (giftIds.has(String(item.product_id))) updates[item.key] = 0;
          continue;
        }
        const left = room.get(String(parent)) || 0;
        const keep = Math.min(item.quantity, left);
        room.set(String(parent), left - keep);
        if (keep !== item.quantity) updates[item.key] = keep;
      }
      if (!Object.keys(updates).length) return;

      const response = await fetch(`${Theme?.routes?.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const updated = await response.json();
      if (!response.ok) throw new Error(updated.description || updated.message || response.statusText);

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: updated,
            sourceId: 'ia-gift-guard',
            data: { source: 'ia-gift-guard', itemCount: updated.item_count },
          },
        })
      );
    } catch (error) {
      if (window.console) console.warn('[ia-gift-guard] could not tidy the cart:', error);
    }
  },
};

if (!window.iaGiftGuard) {
  window.iaGiftGuard = iaGiftGuard;
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'ia-gift-guard') return;
    iaGiftGuard.schedule();
  });
  iaGiftGuard.schedule();
}

/**
 * Search the phrase, not the words.
 *
 * Shopify treats "Ready to wear" as ready OR to OR wear and returns a
 * thousand loosely related things. Quoting it asks for the phrase and returns
 * the couple of hundred a shopper actually meant.
 *
 * Every submitted query is quoted, single words included, so what the shopper
 * typed is what gets searched. This runs on submit only: the suggestions that
 * drop down while typing still go through Shopify's prefix matching, so "read"
 * keeps offering Ready to wear as the shopper types.
 */
function phraseQuery(value) {
  const term = (value || '').trim();
  if (!term || term.includes('"')) return term;
  return `"${term}"`;
}

if (document.documentElement.hasAttribute('data-ia-search-phrase')) {
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!/\/search\b/.test(form.getAttribute('action') || '')) return;

      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const quoted = phraseQuery(input.value);
      if (quoted === input.value) return;

      // The box keeps what the shopper typed — no quotes flashing up on
      // submit, or waiting there on the way back. A hidden twin in its place
      // carries the phrase; the form's data is read straight after this
      // event, so the box gets its name back on the next task.
      const phrase = document.createElement('input');
      phrase.type = 'hidden';
      phrase.name = 'q';
      phrase.value = quoted;
      input.removeAttribute('name');
      input.after(phrase);
      setTimeout(() => {
        phrase.remove();
        input.setAttribute('name', 'q');
      });
    },
    // Capture, so the value is already quoted by the time anything else reads it.
    true
  );
}

/**
 * The product image, full size.
 *
 * A native <dialog>, so the modal behaviour, the backdrop, the focus trap and
 * Escape all come from the browser. The images are a horizontal snap track:
 * one per screen, swipeable on a phone, arrow keys on a desktop.
 */
class IaZoom extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-ia-zoom]');
    this.track = this.querySelector('[data-ia-zoom-track]');
    if (!this.dialog || !this.track) return;

    this.counter = this.querySelector('[data-ia-zoom-counter]');
    this.railButton = this.querySelector('[data-ia-zoom-rail]');
    this.dots = [...this.querySelectorAll('[data-ia-zoom-go]')];

    this.addEventListener('click', this.onClick);
    this.dialog.addEventListener('keydown', this.onKey);
    this.dialog.addEventListener('close', this.onClose);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
  }

  get items() {
    return [...this.track.children];
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const step = target.closest('[data-ia-zoom-step]');
    if (step) return this.step(Number(step.dataset.iaZoomStep));

    const dot = target.closest('[data-ia-zoom-go]');
    if (dot) return this.goTo(dot.dataset.iaZoomGo, 'smooth');

    if (target.closest('[data-ia-zoom-rail]')) return this.toggleRail();
    if (target.closest('[data-ia-zoom-full]')) return this.toggleFullScreen();

    // The photograph magnifies where it was clicked; a second click lets go.
    const img = target.closest('.ia-zoom__img');
    if (img) return this.toggleZoom(img, event);

    // The close button, and the empty space around the photograph.
    if (target.closest('[data-ia-zoom-close]') || target.matches('.ia-zoom__item')) this.dialog.close();
  };

  onKey = (event) => {
    if (event.key === 'ArrowLeft') this.step(-1);
    if (event.key === 'ArrowRight') this.step(1);
  };

  /*
   * Magnifier. The picture scales from its top-left corner and translates so
   * the point under the pointer stays put; the translation is clamped so the
   * picture always covers the box it had before zooming. A mouse pans just by
   * moving; a finger drags. The box is measured once, before scaling, so the
   * maths stays in unscaled pixels.
   */
  static SCALE = 2.5;

  toggleZoom(img, event) {
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    if (this.zoom) return this.unzoom();

    const box = img.getBoundingClientRect();
    this.zoom = { img, box, x: 0, y: 0 };
    // Ask the srcset for a candidate as wide as the picture now is on screen.
    img.sizes = `${Math.ceil(box.width * IaZoom.SCALE)}px`;
    img.classList.add('is-zoomed');
    this.dialog.classList.add('ia-zoom--zoomed');
    this.focusOn(event.clientX - box.left, event.clientY - box.top);

    img.addEventListener('pointerdown', this.onZoomDown);
    img.addEventListener('pointermove', this.onZoomMove);
    img.addEventListener('pointerup', this.onZoomUp);
    img.addEventListener('pointercancel', this.onZoomUp);
  }

  unzoom() {
    const zoom = this.zoom;
    if (!zoom) return;
    this.zoom = null;
    zoom.img.removeEventListener('pointerdown', this.onZoomDown);
    zoom.img.removeEventListener('pointermove', this.onZoomMove);
    zoom.img.removeEventListener('pointerup', this.onZoomUp);
    zoom.img.removeEventListener('pointercancel', this.onZoomUp);
    zoom.img.classList.remove('is-zoomed', 'is-panning', 'is-dragging');
    zoom.img.style.transform = '';
    this.dialog.classList.remove('ia-zoom--zoomed');
  }

  // Keep the point (x, y) of the unscaled picture under the same spot.
  focusOn(x, y) {
    const grow = IaZoom.SCALE - 1;
    this.panTo(-x * grow, -y * grow);
  }

  panTo(x, y) {
    const { img, box } = this.zoom;
    const grow = IaZoom.SCALE - 1;
    this.zoom.x = Math.min(0, Math.max(-box.width * grow, x));
    this.zoom.y = Math.min(0, Math.max(-box.height * grow, y));
    img.style.transform = `translate(${this.zoom.x}px, ${this.zoom.y}px) scale(${IaZoom.SCALE})`;
  }

  onZoomDown = (event) => {
    this.dragged = false;
    if (event.pointerType === 'mouse' || !this.zoom) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    this.drag = { sx: event.clientX, sy: event.clientY, x: this.zoom.x, y: this.zoom.y };
  };

  onZoomMove = (event) => {
    if (!this.zoom) return;
    if (event.pointerType === 'mouse') {
      this.zoom.img.classList.add('is-panning');
      const { box } = this.zoom;
      return this.focusOn(event.clientX - box.left, event.clientY - box.top);
    }
    if (!this.drag) return;
    const dx = event.clientX - this.drag.sx;
    const dy = event.clientY - this.drag.sy;
    // A few pixels of wobble is still a tap, which zooms back out.
    if (!this.dragged && Math.hypot(dx, dy) < 6) return;
    this.dragged = true;
    this.zoom.img.classList.add('is-dragging');
    this.panTo(this.drag.x + dx, this.drag.y + dy);
  };

  onZoomUp = () => {
    this.drag = null;
    this.zoom?.img.classList.remove('is-dragging');
  };

  onScroll = () => {
    clearTimeout(this.tick);
    this.tick = setTimeout(() => this.paint(), 70);
  };

  onClose = () => {
    this.unzoom();
    if (document.fullscreenElement) document.exitFullscreen?.();
  };

  open(id) {
    // Shown first, then scrolled: a dialog still in display:none has no width
    // to scroll by.
    this.dialog.showModal();
    this.goTo(id, 'instant');
  }

  goTo(id, behavior) {
    const item = this.track.querySelector(`[data-ia-zoom-item="${id}"]`);
    if (!item) return;
    this.unzoom();
    this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior });
    this.paint();
  }

  step(by) {
    this.unzoom();
    this.track.scrollBy({ left: by * this.track.clientWidth, behavior: 'smooth' });
  }

  toggleRail() {
    const off = this.dialog.classList.toggle('ia-zoom--no-rail');
    this.railButton?.setAttribute('aria-pressed', off ? 'false' : 'true');
    // The stage just changed width, so the snap offsets did too.
    requestAnimationFrame(() => this.goTo(this.currentId(), 'instant'));
  }

  toggleFullScreen() {
    this.unzoom();
    if (document.fullscreenElement) document.exitFullscreen?.();
    else this.dialog.requestFullscreen?.();
  }

  index() {
    return Math.round(this.track.scrollLeft / (this.track.clientWidth || 1));
  }

  currentId() {
    return this.items[this.index()]?.dataset.iaZoomItem;
  }

  paint() {
    const index = Math.min(this.index(), this.items.length - 1);
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.items.length}`;

    const id = this.items[index]?.dataset.iaZoomItem;
    for (const dot of this.dots) {
      const on = dot.dataset.iaZoomGo === id;
      dot.classList.toggle('is-active', on);
      if (on) dot.scrollIntoView({ block: 'nearest' });
    }
  }
}

if (!customElements.get('ia-zoom')) customElements.define('ia-zoom', IaZoom);

/**
 * Fetches a recommendations band when it comes near the viewport.
 *
 * Shopify only fills the `recommendations` object when the section is asked
 * for through the recommendations route, so the first paint is placeholders
 * and this swaps in the answer. Nothing is requested for a band the visitor
 * never scrolls to.
 */
class IaRecommend extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true' || !this.dataset.url) return;

    this.observer = new IntersectionObserver(this.onNear, { rootMargin: '0px 0px 400px 0px' });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  onNear = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.observer.disconnect();
    this.load();
  };

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      if (!response.ok) throw new Error(response.statusText);

      const markup = await response.text();
      const fresh = new DOMParser().parseFromString(markup, 'text/html').querySelector('ia-recommend');
      // No pairs set for this product: the band removes itself rather than
      // leaving a heading over an empty row.
      if (!fresh || !fresh.children.length) {
        this.closest('.ia-section')?.remove();
        return;
      }
      this.replaceChildren(...fresh.childNodes);
      this.dataset.ready = 'true';
    } catch {
      this.closest('.ia-section')?.remove();
    }
  }
}

if (!customElements.get('ia-carousel')) customElements.define('ia-carousel', IaCarousel);
if (!customElements.get('ia-view')) customElements.define('ia-view', IaView);
/**
 * The saved-items page.
 *
 * The hearts only ever wrote to localStorage, so the server has no idea what a
 * shopper saved. Rather than one request per product, the saved ids are handed
 * to the search endpoint as `id:1 OR id:2 …`, which renders the wishlist
 * section with those products and returns the finished grid — one round trip
 * regardless of how many items are saved.
 *
 * Search returns its own relevance order, so the cards are put back into the
 * order they were saved in before being shown. Unhearting a card removes it
 * here and now, without a reload.
 */
class IaWishlistPage extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-ia-wish-body]');
    this.empty = this.querySelector('[data-ia-wish-empty]');
    this.counter = this.querySelector('[data-ia-wish-count]');
    if (!this.body) return;

    this.addEventListener('ia:wishlist', (event) => {
      if (!event.detail?.saved) this.drop(String(event.detail.id));
    });

    this.load();
  }

  get ids() {
    const max = Number(this.dataset.max) || 50;
    return [...IaWishlist.read()].map(String).slice(0, max);
  }

  async load() {
    const ids = this.ids;
    if (!ids.length) return this.showEmpty(true);

    const url = new URL(this.dataset.endpoint || '/search', location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', this.dataset.section);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`search ${response.status}`);
      const grid = new DOMParser()
        .parseFromString(await response.text(), 'text/html')
        .querySelector('[data-ia-wish-grid]');
      if (!grid || !grid.children.length) return this.showEmpty(true);

      this.order(grid, ids);
      this.body.replaceChildren(grid);
      this.showEmpty(false);
    } catch {
      // The saved ids are still in localStorage; an empty state is a better
      // answer than a half-rendered page.
      this.showEmpty(true);
    }
  }

  /** Puts the cards back into the order the shopper saved them in. */
  order(grid, ids) {
    const rank = new Map(ids.map((id, index) => [id, index]));
    [...grid.children]
      .sort((a, b) => (rank.get(a.dataset.productId) ?? 0) - (rank.get(b.dataset.productId) ?? 0))
      .forEach((li) => grid.append(li));
  }

  drop(id) {
    this.body.querySelector(`[data-product-id="${id}"]`)?.remove();
    const left = this.body.querySelectorAll('[data-product-id]').length;
    if (!left) this.showEmpty(true);
    else this.count(left);
  }

  count(n) {
    if (!this.counter) return;
    this.counter.hidden = false;
    this.counter.textContent = n === 1 ? '1 item' : `${n} items`;
  }

  showEmpty(isEmpty) {
    if (this.empty) this.empty.hidden = !isEmpty;
    this.body.hidden = isEmpty;
    if (isEmpty) {
      this.body.replaceChildren();
      if (this.counter) this.counter.hidden = true;
    } else {
      this.count(this.body.querySelectorAll('[data-product-id]').length);
    }
  }
}

if (!customElements.get('ia-facets')) customElements.define('ia-facets', IaFacets);
if (!customElements.get('ia-sheet')) customElements.define('ia-sheet', IaSheet);
if (!customElements.get('ia-infinite-cap')) customElements.define('ia-infinite-cap', IaInfiniteCap);
if (!customElements.get('ia-tag-filter')) customElements.define('ia-tag-filter', IaTagFilter);
if (!customElements.get('ia-countdown')) customElements.define('ia-countdown', IaCountdown);
if (!customElements.get('ia-load-more')) customElements.define('ia-load-more', IaLoadMore);
if (!customElements.get('ia-add-to-cart')) customElements.define('ia-add-to-cart', IaAddToCart);
if (!customElements.get('ia-drawer')) customElements.define('ia-drawer', IaDrawer);
if (!customElements.get('ia-wishlist')) customElements.define('ia-wishlist', IaWishlist);
if (!customElements.get('ia-wishlist-page')) customElements.define('ia-wishlist-page', IaWishlistPage);
if (!customElements.get('ia-tabs')) customElements.define('ia-tabs', IaTabs);
if (!customElements.get('ia-product')) customElements.define('ia-product', IaProduct);
if (!customElements.get('ia-recommend')) customElements.define('ia-recommend', IaRecommend);
if (!customElements.get('ia-video')) customElements.define('ia-video', IaVideo);
// After ia-video, so the frames inside a slideshow are upgraded when it starts.
if (!customElements.get('ia-film')) customElements.define('ia-film', IaFilm);



/**
 * Product page: the thumbnail strip's arrows and the description's
 * Read more. Both are plain delegated clicks, so a re-rendered section
 * needs no re-wiring.
 */
function iaThumbRails() {
  for (const rail of document.querySelectorAll('[data-ia-thumbrail]')) {
    const strip = rail.querySelector('.ia-pdp__thumbs');
    if (!strip) continue;
    // A left-hand strip scrolls down, not across (desktop only; on a phone
    // the same strip is laid out in a row again).
    const vertical = () => rail.classList.contains('ia-pdp__thumbrail--vertical') && getComputedStyle(strip).flexDirection === 'column';
    const update = () => {
      const v = vertical();
      const size = v ? strip.clientHeight : strip.clientWidth;
      const full = v ? strip.scrollHeight : strip.scrollWidth;
      const pos = v ? strip.scrollTop : strip.scrollLeft;
      rail.classList.toggle('is-scrollable', full > size + 2);
      rail.querySelector('[data-ia-thumb-step="-1"]')?.toggleAttribute('disabled', pos <= 1);
      rail.querySelector('[data-ia-thumb-step="1"]')?.toggleAttribute('disabled', pos + size >= full - 1);
    };
    if (!rail.dataset.iaBound) {
      rail.dataset.iaBound = 'true';
      strip.addEventListener('scroll', update, { passive: true });
      new ResizeObserver(update).observe(strip);
      // Keep the highlighted thumbnail in view when the gallery moves on.
      new MutationObserver(() => {
        const active = strip.querySelector('.is-active');
        if (!active) return;
        if (vertical()) {
          const a = active.offsetTop;
          const b = a + active.offsetHeight;
          if (a < strip.scrollTop || b > strip.scrollTop + strip.clientHeight) {
            strip.scrollTo({ top: a - (strip.clientHeight - active.offsetHeight) / 2, behavior: 'smooth' });
          }
          return;
        }
        const a = active.offsetLeft;
        const b = a + active.offsetWidth;
        if (a < strip.scrollLeft || b > strip.scrollLeft + strip.clientWidth) {
          strip.scrollTo({ left: a - (strip.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
        }
      }).observe(strip, { attributes: true, subtree: true, attributeFilter: ['class'] });
    }
    update();
  }
}

function iaReadMore() {
  for (const box of document.querySelectorAll('[data-ia-more]')) {
    const body = box.querySelector('[data-ia-more-body]');
    const button = box.querySelector('[data-ia-more-toggle]');
    if (!body || !button) continue;
    const limit = Number(box.dataset.iaMore) || 0;
    const long = limit > 0 && body.scrollHeight > limit + 24;
    box.classList.toggle('is-clamped', long && !box.classList.contains('is-open'));
    button.hidden = !long;
  }
}

// Pressing an arrow starts the picture's download before the click lands.
document.addEventListener('pointerdown', (event) => {
  const galStep = event.target.closest?.('[data-ia-gal-step]');
  if (galStep) galStep.closest('ia-product')?.warmMedia(Number(galStep.dataset.iaGalStep));
});

document.addEventListener('click', (event) => {
  const galStep = event.target.closest?.('[data-ia-gal-step]');
  if (galStep) {
    galStep.closest('ia-product')?.stepMedia(Number(galStep.dataset.iaGalStep));
    return;
  }
  const step = event.target.closest?.('[data-ia-thumb-step]');
  if (step) {
    const strip = step.closest('[data-ia-thumbrail]')?.querySelector('.ia-pdp__thumbs');
    if (strip) {
      const d = Number(step.dataset.iaThumbStep);
      if (getComputedStyle(strip).flexDirection === 'column') strip.scrollBy({ top: d * strip.clientHeight * 0.8, behavior: 'smooth' });
      else strip.scrollBy({ left: d * strip.clientWidth * 0.8, behavior: 'smooth' });
    }
    return;
  }
  const toggle = event.target.closest?.('[data-ia-more-toggle]');
  if (toggle) {
    const box = toggle.closest('[data-ia-more]');
    const open = !box.classList.contains('is-open');
    box.classList.toggle('is-open', open);
    box.classList.toggle('is-clamped', !open);
    toggle.textContent = open ? toggle.dataset.less : toggle.dataset.more;
    toggle.setAttribute('aria-expanded', String(open));
    if (!open) box.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
});

const iaPdpInit = () => {
  iaThumbRails();
  iaReadMore();
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iaPdpInit);
else iaPdpInit();
window.addEventListener('load', iaReadMore);
document.addEventListener('shopify:section:load', iaPdpInit);



/** Footer on phones: a column heading opens and closes its links. */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest?.('[data-ia-footer-toggle]');
  if (!toggle) return;
  const column = toggle.closest('.ia-footer__column');
  const open = !column.classList.contains('is-open');
  column.classList.toggle('is-open', open);
  toggle.setAttribute('aria-expanded', String(open));
});


/**
 * Mobile bottom bar (sections/ia-mobile-dock.liquid).
 *
 * Every button drives what the theme already has: the header's menu drawer,
 * Horizon's search modal (declaratively, through on:click) and cart drawer.
 * Account opens the bar's own sheet. Whichever is open lights its button —
 * watched on the dialogs' open attribute, so it stays right however they were
 * opened or closed. The cart count mirrors the header's, which Horizon keeps
 * in sessionStorage after every cart change.
 */
class IaDock extends HTMLElement {
  connectedCallback() {
    this.sheet = this.querySelector('[data-ia-dock-sheet]');
    this.veil = this.querySelector('[data-ia-dock-veil]');
    this.count = this.querySelector('[data-ia-dock-count]');

    this.addEventListener('click', this.onClick);
    this.veil?.addEventListener('click', () => this.closeAll());
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('cart:update', this.onCart);
    window.addEventListener('pageshow', this.onCart);

    this.observer = new MutationObserver(() => this.sync());
    requestAnimationFrame(() => {
      for (const dialog of [this.menuDialog, this.cartDialog, this.searchDialog]) {
        if (dialog) this.observer.observe(dialog, { attributes: true, attributeFilter: ['open', 'class'] });
      }
      this.sync();
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('cart:update', this.onCart);
    window.removeEventListener('pageshow', this.onCart);
  }

  /** Displayed at this width (the section hides it above its breakpoint). */
  isShown() {
    return getComputedStyle(this).display !== 'none';
  }

  get drawer() {
    return document.querySelector('ia-drawer.ia-header');
  }

  get menuDialog() {
    return this.drawer?.dialog ?? null;
  }

  get cartDialog() {
    return document.querySelector('cart-drawer-component dialog');
  }

  get searchDialog() {
    return document.querySelector('#search-modal dialog') ?? document.querySelector('#search-modal');
  }

  get sheetOpen() {
    return Boolean(this.sheet?.classList.contains('is-open'));
  }

  onClick = (event) => {
    const button = event.target.closest('[data-ia-dock]');
    if (!button) return;

    switch (button.dataset.iaDock) {
      case 'menu': {
        const drawer = this.drawer;
        if (!drawer?.dialog) return;
        if (drawer.dialog.open) {
          drawer.close();
        } else {
          this.closeSheet();
          drawer.open();
        }
        break;
      }
      case 'account':
        if (this.sheetOpen) this.closeSheet();
        else this.openSheet();
        break;
      case 'cart': {
        const cart = document.querySelector('cart-drawer-component');
        if (!button.hasAttribute('data-drawer') || typeof cart?.open !== 'function') return;
        event.preventDefault();
        this.closeAll();
        cart.open();
        break;
      }
      case 'search':
        // Horizon's document listener has already opened the modal.
        this.closeMenu();
        this.closeSheet();
        break;
    }
  };

  onKey = (event) => {
    if (event.key !== 'Escape') return;
    if (this.sheetOpen || this.menuDialog?.classList.contains('ia-menu--docked')) this.closeAll();
  };

  openSheet() {
    if (!this.sheet) return;
    this.closeMenu();
    this.sheet.inert = false;
    this.sheet.classList.add('is-open');
    document.documentElement.style.setProperty('overflow', 'hidden');
    this.sheet.querySelector('a, summary, button')?.focus({ preventScroll: true });
    this.sync();
  }

  closeSheet() {
    if (!this.sheetOpen) return;
    this.sheet.classList.remove('is-open');
    this.sheet.inert = true;
    if (!this.menuDialog?.open) document.documentElement.style.removeProperty('overflow');
    this.sync();
  }

  closeMenu() {
    if (this.menuDialog?.open) this.drawer.close();
  }

  closeAll() {
    this.closeMenu();
    this.closeSheet();
  }

  sync() {
    const menu = this.menuDialog;
    const menuOn = Boolean(menu?.open && !menu.classList.contains('ia-menu--closing'));
    const docked = menuOn && menu.classList.contains('ia-menu--docked');
    const set = (key, on) => {
      const button = this.querySelector(`[data-ia-dock="${key}"]`);
      if (!button) return;
      button.classList.toggle('is-on', on);
      if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', String(on));
    };
    set('menu', menuOn);
    set('account', this.sheetOpen);
    set('cart', Boolean(this.cartDialog?.open));
    set('search', Boolean(this.searchDialog?.open));
    this.classList.toggle('is-veiled', this.sheetOpen);
    this.classList.toggle('is-catching', docked && !this.sheetOpen);
  }

  onCart = () => {
    if (!this.count) return;
    // After the header's own bubble has taken the update.
    requestAnimationFrame(() => {
      let value = null;
      try {
        value = JSON.parse(sessionStorage.getItem('cart-count') || 'null')?.value ?? null;
      } catch (error) {
        value = null;
      }
      if (value === null) return;
      const n = Number(value) || 0;
      this.count.textContent = String(n);
      if (this.count.dataset.zero === 'hide') this.count.hidden = n === 0;
    });
  };
}
if (!customElements.get('ia-dock')) customElements.define('ia-dock', IaDock);


/**
 * PLP: back from a product lands on the same products, already loaded.
 *
 * The back-forward cache would do this for free, but third-party frames on
 * the store keep pages out of it, so the theme keeps its own copy. Each page
 * that infinite scroll appends is recorded as its pristine card markup — taken
 * from the HTML Horizon fetched (results-list .pages), in idle time, never
 * from the live, script-touched DOM. When the page is left, those cards and
 * the card at the top of the screen go into sessionStorage for this URL (the
 * last three collections, for half an hour); the inline script after the grid
 * (ia-plp-body) puts them back before the first paint. No request is made.
 */
(() => {
  const KEY = 'ia:plp:';
  const INDEX = 'ia:plp-index';
  const KEEP = 3;
  const cards = new Map();
  const list = () => document.querySelector('results-list');
  const gridOf = (el) => el?.querySelector('[ref="grid"]');

  // Seed with what the inline script restored, which Horizon never fetched.
  const restored = window.iaPlpRestored;
  if (restored?.pages?.length && restored.sizes) {
    let at = 0;
    restored.pages.forEach((page, i) => {
      cards.set(page, restored.html.slice(at, at + restored.sizes[i]));
      at += restored.sizes[i];
    });
  }

  const collect = () => {
    const el = list();
    const grid = gridOf(el);
    if (!grid || !el.pages) return;
    for (const card of grid.querySelectorAll(':scope > [data-page]')) {
      const page = Number(card.dataset.page);
      if (cards.has(page) || !el.pages.has(page)) continue;
      const doc = new DOMParser().parseFromString(el.pages.get(page), 'text/html');
      // Runs of whitespace (Liquid's indentation) collapse to one space: the
      // same rendering, about half the storage.
      const html = [...doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]')].map((li) => li.outerHTML).join('').replace(/\s{2,}/g, ' ');
      if (html) cards.set(page, html);
    }
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    idle(() => {
      queued = false;
      collect();
    });
  };

  const start = () => {
    const grid = gridOf(list());
    if (!grid) return false;
    new MutationObserver(queue).observe(grid, { childList: true });
    return true;
  };
  if (!start()) document.addEventListener('DOMContentLoaded', start, { once: true });
  // New filter results: the recorded pages belong to the old ones.
  document.addEventListener('filter:update', () => cards.clear());

  window.addEventListener('pagehide', () => {
    const grid = gridOf(list());
    if (!grid) return;
    const key = KEY + location.pathname + location.search;
    try {
      collect();
      const items = [...grid.querySelectorAll(':scope > [data-page]')];
      if (!items.length) return;
      const first = Number(items[0].dataset.page);
      const pages = [...new Set(items.map((li) => Number(li.dataset.page)))].filter((p) => p !== first).sort((a, b) => a - b);
      if (pages.some((p) => !cards.has(p))) return; // not all recorded yet: let the browser do what it can
      // The card at the top of the screen, below a sticky header.
      const header = document.getElementById('header-group');
      const top = header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().bottom : 0;
      let index = items.findIndex((li) => li.getBoundingClientRect().bottom > top);
      if (index < 0) index = items.length - 1;
      if (!pages.length && window.scrollY < 100) {
        sessionStorage.removeItem(key);
        return;
      }
      const parts = pages.map((p) => cards.get(p));
      const data = { t: Date.now(), first, pages, parts, html: parts.join(''), index, offset: Math.round(items[index].getBoundingClientRect().top), y: Math.round(window.scrollY) };
      // parts + html would double the size: keep html, rebuild parts on read.
      const payload = JSON.stringify({ ...data, parts: undefined, sizes: parts.map((p) => p.length) });
      sessionStorage.setItem(key, payload);
      const keys = JSON.parse(sessionStorage.getItem(INDEX) || '[]').filter((k) => k !== key);
      keys.push(key);
      while (keys.length > KEEP) sessionStorage.removeItem(keys.shift());
      sessionStorage.setItem(INDEX, JSON.stringify(keys));
      // We place the page ourselves on the way back.
      history.scrollRestoration = 'manual';
    } catch (error) {
      try { sessionStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
    }
  });
})();


/**
 * Product cards: the second (hover) picture is fetched the first time a mouse
 * rests on the card, not with the page (see ia-product-card). Touch never
 * triggers it, so phones skip those downloads entirely.
 */
document.addEventListener('pointerover', (event) => {
  if (event.pointerType === 'touch') return;
  const card = event.target.closest?.('.ia-card');
  const img = card?.querySelector('.ia-card__img--hover[data-srcset], .ia-card__img--hover[data-src]');
  if (!img) return;
  if (img.dataset.srcset) img.srcset = img.dataset.srcset;
  if (img.dataset.src) img.src = img.dataset.src;
  img.removeAttribute('data-srcset');
  img.removeAttribute('data-src');
}, { passive: true });

/**
 * Every lazy picture on the site starts loading before it scrolls in.
 *
 * Native lazy loading starts an image only shortly before it reaches the
 * screen, at a distance the browser picks. Here any lazy <img> within the
 * distance set in Theme settings > Ideas performance (data-ia-lazy-ahead on
 * <html>, in screen heights) is switched to eager, so what the shopper is about
 * to see is already there; images further away still wait. Pictures inside
 * hidden parts (a closed menu or drawer) never intersect and are left alone,
 * and hover pictures without a source yet (data-src) are skipped.
 */
(() => {
  const ahead = Number(document.documentElement.dataset.iaLazyAhead);
  if (!('IntersectionObserver' in window) || !Number.isFinite(ahead)) return;
  const warm = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        warm.unobserve(entry.target);
        entry.target.loading = 'eager';
      }
    },
    { rootMargin: `0px 0px ${ahead}% 0px` }
  );
  const SELECTOR = 'img[loading="lazy"]:not([data-src]):not([data-srcset])';
  const seen = new WeakSet();
  const scan = (root) => {
    if (root.matches?.(SELECTOR) && !seen.has(root)) {
      seen.add(root);
      warm.observe(root);
    }
    for (const img of root.querySelectorAll?.(SELECTOR) ?? []) {
      if (seen.has(img)) continue;
      seen.add(img);
      warm.observe(img);
    }
  };
  const start = () => {
    scan(document);
    // Pictures added later: infinite scroll pages, filter results, section reloads.
    new MutationObserver((records) => {
      for (const r of records) for (const n of r.addedNodes) if (n.nodeType === 1) scan(n);
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

/**
 * Menu drawer pictures, fetched before the drawer opens.
 *
 * They are lazy and sit in a closed <dialog>, so the browser only started
 * them when the drawer opened — the menu appeared, then its pictures arrived.
 * Now: the first pane's pictures load once the page is idle after load, and
 * all of them the moment a pointer, finger or keyboard heads for a menu button.
 */
(() => {
  const eager = (root) => {
    for (const img of root.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';
  };
  const drawer = () => document.querySelector('[data-ia-drawer-dialog]');
  let all = false;
  const warmAll = (event) => {
    if (all || !event.target.closest?.('[data-ia-drawer-open], [data-ia-dock="menu"]')) return;
    const d = drawer();
    if (!d) return;
    all = true;
    eager(d);
  };
  for (const type of ['pointerover', 'touchstart', 'focusin']) document.addEventListener(type, warmAll, { passive: true });

  const warmFirst = () => {
    const d = drawer();
    if (!d || all) return;
    // The rail and the pane a desktop drawer opens on, plus the phone's top level.
    const first = d.querySelector('[data-ia-pane]');
    eager(d.querySelector('.ia-menu__roots')?.parentElement || d);
    if (first) eager(first);
  };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
  const afterLoad = () => idle(warmFirst, { timeout: 4000 });
  if (document.readyState === 'complete') afterLoad();
  else window.addEventListener('load', afterLoad, { once: true });
})();

// ---- Payment partner popups (snippets/ia-instalments.liquid) ----
/*
 * Partner details popups: one delegated listener for every row on the page
 * (product page and mini cart), opened as a modal <dialog>. A click on the
 * dimmed backdrop, the close button or Escape closes it.
 *
 * The popup's button can follow a link template from the theme settings
 * (Theme settings > Ideas payment partners > Button link template). Nothing
 * here knows about any one partner: the template carries the address and
 * its parameters, and these placeholders are filled in at the click:
 *   [items_base64]  the items as base64 JSON, {"variantId": quantity}
 *   [items_json]    the same JSON, URL-encoded
 *   [variant_id]    the first item's variant id
 *   [quantity]      the first item's quantity
 * On the product page the item is the variant and quantity selected at that
 * moment; in the mini cart, the cart's items as rendered.
 */
if (!window.iaInstalPopups) {
  window.iaInstalPopups = true;

  const instalItems = (link) => {
    let items = {};
    try {
      items = JSON.parse(link.dataset.items || '{}');
    } catch {
      items = {};
    }

    const product = link.closest('ia-product');
    if (product) {
      const variant = product.querySelector('[data-ia-variant-id]')?.value;
      const quantity = Math.max(1, Number(product.querySelector('[name="quantity"]')?.value) || 1);
      if (variant) items = { [variant]: quantity };
    }
    return items;
  };

  const instalHref = (link) => {
    const template = link.dataset.iaInstalLink;
    if (!template) return null;
    const items = instalItems(link);
    const [first] = Object.entries(items);
    if (!first) return null;

    const json = JSON.stringify(items);
    return template
      .replaceAll('[items_base64]', encodeURIComponent(btoa(json)))
      .replaceAll('[items_json]', encodeURIComponent(json))
      .replaceAll('[variant_id]', encodeURIComponent(first[0]))
      .replaceAll('[quantity]', encodeURIComponent(first[1]));
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-ia-instal-link]');
    if (link) {
      const href = instalHref(link);
      if (href) link.href = href;
      else event.preventDefault();
      return;
    }

    const open = event.target.closest('[data-ia-instal-open]');
    if (open) {
      const dialog = document.getElementById(open.dataset.iaInstalOpen);
      if (!dialog || dialog.open) return;
      // A shared popup (product cards) takes the opener's amount and item.
      if (open.dataset.amount) {
        for (const node of dialog.querySelectorAll('[data-ia-instal-each]')) {
          if (typeof iaSetMoney === 'function') iaSetMoney(node, open.dataset.amount);
          else node.textContent = open.dataset.amount;
        }
      }
      if (open.dataset.items) {
        for (const link of dialog.querySelectorAll('[data-ia-instal-link]')) link.dataset.items = open.dataset.items;
      }
      dialog.showModal();
      return;
    }

    const close = event.target.closest('[data-ia-instal-close]');
    if (close) {
      close.closest('dialog')?.close();
      return;
    }

    // A click that lands on the <dialog> itself is on the backdrop.
    if (event.target instanceof HTMLDialogElement && event.target.classList.contains('ia-instal-pop')) {
      event.target.close();
    }
  });
}

// ---- Mobile menu pills (snippets/ia-header-pills.liquid) ----
/**
 * <ia-pills>: a scrolling row with arrows and dots. Arrows and dots only
 * appear when the pills overflow; the open item is scrolled into view.
 */
if (!customElements.get('ia-pills')) {
  class IaPills extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-dir="-1"]');
      this.next = this.querySelector('[data-dir="1"]');
      this.dotsBox = this.querySelector('[data-dots]');
      if (!this.track) return;

      for (const button of [this.prev, this.next]) {
        button?.addEventListener('click', () => {
          const dir = Number(button.dataset.dir);
          this.track.scrollBy({ left: dir * this.track.clientWidth * 0.8, behavior: 'smooth' });
        });
      }

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.sync();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);

      const active = this.track.querySelector('.is-active');
      if (active) this.track.scrollLeft = active.offsetLeft - (this.track.clientWidth - active.offsetWidth) / 2;
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    /** One dot per screenful of pills. */
    build() {
      const overflow = this.track.scrollWidth - this.track.clientWidth > 2;
      this.classList.toggle('is-scrollable', overflow);
      const pages = overflow ? Math.ceil(this.track.scrollWidth / this.track.clientWidth) : 0;

      if (this.dotsBox && this.dotsBox.childElementCount !== pages) {
        this.dotsBox.replaceChildren(...Array.from({ length: pages }, () => document.createElement('span')));
      }
      this.sync();
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const at = this.track.scrollLeft;
      if (this.prev) this.prev.hidden = max <= 2 || at <= 2;
      if (this.next) this.next.hidden = max <= 2 || at >= max - 2;

      const dots = this.dotsBox ? [...this.dotsBox.children] : [];
      if (!dots.length) return;
      const index = max > 0 ? Math.round((at / max) * (dots.length - 1)) : 0;
      dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
    }
  }

  customElements.define('ia-pills', IaPills);
}
